/* ============================================================
   ILS — Cart drawer (custom)
   - Bind .ils-cart click to open <cart-drawer>
   - Keep .ils-cart__count in sync with cart state
   - Intercept .rm clicks → confirm modal → trigger Dawn AJAX remove
   - Populate upsell from /recommendations/products.json
   - Rewards bars (free shipping / mango gift / giveaway chances)
   - Weight (variant) selector with AJAX cart change
   - Continue shopping link → close drawer, stay on current page (TASK-2961)
   - Records last non-cart URL in sessionStorage for the cart page's back link
   ============================================================ */
(function () {
  'use strict';

  const ILS_KNOWN_LOCALE_PREFIXES = [
    '/en-eu', '/ru-eu',
    '/de-de', '/ru-de',
    '/lv-lv', '/ru-lv',
    '/et-ee', '/ru-ee',
    '/lt-lt', '/ru-lt'
  ];
  function localeRoot() {
    try {
      const r = window.Shopify && Shopify.routes && Shopify.routes.root;
      if (typeof r === 'string' && r.length > 0) {
        const trimmed = r.replace(/\/+$/, '');
        return trimmed === '' ? '' : trimmed;
      }
    } catch (e) {}
    const path = (window.location && location.pathname) || '';
    for (let i = 0; i < ILS_KNOWN_LOCALE_PREFIXES.length; i++) {
      const pfx = ILS_KNOWN_LOCALE_PREFIXES[i];
      if (path === pfx || path.indexOf(pfx + '/') === 0) return pfx;
    }
    return '';
  }

  /* ===== TASK-2961 — "Continue shopping" returns to the previous page =====
     Reaching the /cart PAGE is a real navigation, so its "Continue shopping"
     button must send the shopper back to where they came from — the last
     page they viewed before opening the cart, NOT a fixed /collections/all.
     We record that URL here on every non-cart page load (this script is in
     the global header, so it runs everywhere). custom-cart-page.js reads it
     back. The cart DRAWER is an overlay and simply closes — no navigation,
     so it inherently keeps the shopper on the page they opened it from. */
  const ILS_CONTINUE_KEY = 'ILS_CONTINUE_URL';
  function ilsStripLocale(path) {
    const root = localeRoot();
    if (root && (path === root || path.indexOf(root + '/') === 0)) {
      const rest = path.slice(root.length);
      return rest === '' ? '/' : rest;
    }
    return path;
  }
  function ilsIsCartPath(path) {
    const p = ilsStripLocale(path || '');
    return p === '/cart' || p.indexOf('/cart/') === 0;
  }
  (function recordContinueUrl() {
    try {
      const path = (window.location && location.pathname) || '';
      if (ilsIsCartPath(path)) return; // never record the cart page itself
      // TASK-3348 — never record the delivery-selector page either. The cart's
      // Checkout button redirects shoppers INTO it (TASK-3340/3345) and its own
      // "Back" already returns to /cart (TASK-3347). If we recorded the selector
      // here, the cart's "Continue shopping" — which replays ILS_CONTINUE_URL —
      // would bounce the shopper straight back to the selector (the reported bug).
      // Detected by the section root, NOT the URL: this script is deferred so the
      // DOM is fully parsed when it runs, and matching the element is locale-proof
      // (the page handle is localized per market — TASK-3343 lv=izvelieties-piegadi,
      // de=lieferung-waehlen, ru=vybor-dostavki — so a path check would miss them).
      if (document.querySelector('[data-ils-dsel]')) return;
      sessionStorage.setItem(ILS_CONTINUE_KEY, location.pathname + location.search);
    } catch (e) {}
  })();

  function moneyFromCents(cents) {
    /* TASK-5795 — these cents are cart.total_price / a line price, i.e.
       PRESENTMENT minor units. On the six local-currency markets the legacy
       body below printed them with a euro sign: a 56,00 kr product price read
       "€56,00". ILSMoney.fromCents() prints them in the format Shopify itself
       renders on the same page, derived from Shopify's own `| money` output.
       It returns null when the oracle is absent, disabled or unsure — and then
       the legacy body runs unchanged, so EUR markets and any failure mode keep
       exactly today's bytes. */
    if (window.ILSMoney && window.ILSMoney.on()) {
      var _p5795 = window.ILSMoney.fromCents(cents);
      if (_p5795 !== null) return _p5795;
    }
    const sign = cents < 0 ? '-' : '';
    const abs = Math.abs(cents | 0);
    const euros = Math.floor(abs / 100);
    const c = String(abs % 100).padStart(2, '0');
    const eFormatted = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    /* TASK-5946 — display only: a FULLY zero fraction prints as no fraction. */
    return sign + '€' + eFormatted + (c === '00' ? '' : ',' + c);
  }

  // TASK-2567: localized "Save €X" label for the WE RECOMMEND upsell cards.
  // The translated `custom.product.save_amount` template (e.g. DE
  // "Sparen Sie __AMOUNT__") is exposed on the upsell host as data-save-tpl,
  // with the money slot as a brace-free sentinel so Liquid can carry it safely
  // in an HTML attribute. The amount is formatted as PLAIN TEXT (moneyFromCents),
  // never HTML, so it cannot leak markup (the TASK-2565 bug class). Falls back
  // to English "Save €X" if the host/attr is absent.
  function upsellSaveLabel(diffCents) {
    const amount = moneyFromCents(diffCents);
    const host = document.querySelector('[data-ils-cd-upsell]');
    const tpl = host && host.getAttribute('data-save-tpl');
    if (tpl && /__AMOUNT__|\{\{\s*amount\s*\}\}/.test(tpl)) {
      return tpl.replace(/__AMOUNT__|\{\{\s*amount\s*\}\}/g, amount);
    }
    return 'Save ' + amount;
  }

  function formatWeight(rawValue, langIso) {
    if (window.ILS && typeof window.ILS.formatWeight === 'function') {
      return window.ILS.formatWeight(rawValue, langIso);
    }
    const src = (rawValue == null ? '' : String(rawValue));
    const m = src.match(/\d+/);
    if (!m) return src;
    let grams = parseInt(m[0], 10);
    if (/kg|кг/i.test(src)) grams *= 1000; /* TASK-2632: cyrillic "кг" on ru markets */
    const lang = String(langIso || (window.Shopify && Shopify.locale) || (document.documentElement && document.documentElement.lang) || '').toLowerCase();
    const isRu = lang.indexOf('ru') === 0;
    const uG = isRu ? 'г' : 'g';
    const uKg = isRu ? 'кг' : 'kg';
    if (grams < 1000) return grams + ' ' + uG;
    const kgInt = Math.floor(grams / 1000);
    const rem = grams % 1000;
    if (rem === 0) return kgInt + ' ' + uKg;
    const frac = String(rem).padStart(3, '0').replace(/0+$/, '');
    return kgInt + ',' + frac + ' ' + uKg;
  }

  function wireCartIconOpener() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    document.addEventListener('click', function (e) {
      const trigger = e.target.closest('.ils-cart');
      if (!trigger) return;
      e.preventDefault();
      if (typeof drawer.open === 'function') drawer.open(trigger);
    });
  }

  function updateCartCount(itemCount) {
    document.querySelectorAll('.ils-cart__count, [data-ils-cart-count]').forEach((el) => {
      el.textContent = itemCount;
      el.classList.toggle('is-shown', itemCount > 0);
    });
    document.querySelectorAll('[data-ils-cd-count-num]').forEach((el) => {
      el.textContent = String(itemCount);
    });
  }

  function fetchAndUpdateCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        if (cart && typeof cart.item_count === 'number') updateCartCount(cart.item_count);
        if (cart && typeof cart.total_price === 'number') updateRewardsFromTotalCents(cart.total_price);
        return cart;
      })
      .catch(() => {});
  }

  function wireCartUpdateSub() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartData = event && event.cartData;
        if (cartData && typeof cartData.item_count === 'number') {
          updateCartCount(cartData.item_count);
        }
        if (cartData && typeof cartData.total_price === 'number') {
          updateRewardsFromTotalCents(cartData.total_price);
        }
        setTimeout(() => {
          fetchAndUpdateCartCount();
          initRewards();
          loadUpsell();
          hydrateLeader();
          initUpsellWave();
        }, 0);
      });
    }
    document.addEventListener('cart:update', fetchAndUpdateCartCount);
  }

  /* TASK-2399 — Free-shipping threshold is dynamic: read per-country from the
     JSON emitted by snippets/custom-cart-rewards.liquid (backed by shop
     metafield `custom.shipping_thresholds`, mirroring the minimum "TOTAL_PRICE
     >= X" condition on any rate=0 method in Shopify Delivery Profiles for the
     country). If the current country is NOT in the map, the Free-shipping bar
     is hidden entirely (never rendered), never re-appears. Mango-gift and
     Giveaway-chances tiers stay code-driven because they are ILS-brand
     mechanics, not Shopify Delivery data. */
  /* TASK-5954 — `eur` is the tier's identity (its rung in the approved
     contract) and never changes. `at` is that rung ON THE EUR AXIS for the
     CURRENT market, and is rewritten by syncTierAxis() before every repaint:
     on a fixed-local market it becomes the approved local amount divided by
     the same rate the cart is divided by, so every `cartEur >= x.at` in this
     file silently becomes "has the cart reached the approved local amount",
     with no second currency axis threaded through the renderer.

     Printing is NOT done from `at` — rwTier() reads the approved amount
     straight out of the matrix by `eur`. */
  const MANGO = [
    { eur: 60,  at: 60,  prize: '125g' },
    { eur: 80,  at: 80,  prize: '250g' },
    { eur: 100, at: 100, prize: '500g' },
    { eur: 150, at: 150, prize: '750g' },
    { eur: 200, at: 200, prize: '1000g' },
  ];
  const GIVE = [
    { eur: 80,  at: 80,  prize: '×1' },
    { eur: 90,  at: 90,  prize: '×2' },
    { eur: 100, at: 100, prize: '×3' },
    { eur: 110, at: 110, prize: '×4' },
    { eur: 120, at: 120, prize: '×5' },
  ];

  /* Resolve `at` for the current market. Called at the top of every
     visibility computation and every repaint, because a market switch
     mid-session changes the answer and the panel must not keep a stale axis.
     On a EUR market (and whenever the oracle refuses) `tierEur` returns null
     and `at` stays the EUR tier — byte-identical behaviour to before. */
  function syncTierAxis() {
    var R = window.ILSRewardMatrix;
    var map = function (row) {
      var v = null;
      if (R && typeof R.tierEur === 'function') {
        try { v = R.tierEur(row.eur); } catch (e) { v = null; }
      }
      row.at = (typeof v === 'number' && isFinite(v) && v > 0) ? v : row.eur;
    };
    MANGO.forEach(map);
    GIVE.forEach(map);
  }

  /* TASK-5795 — THE conversion point. cart.total_price is PRESENTMENT minor
     units; MANGO / GIVE / the free-shipping threshold / the weekly leader are
     all EUR. The whole live defect was that this division by 100 was treated as
     a number of euros, so on /en-se/ a 150,00 kr cart (EUR 13.22) read as
     "EUR 150" and completed all three reward bars.

     The divisor is Shopify's own published window.Shopify.currency.rate — the
     SAME value, read through the SAME fail-safe rule, that the live gift engine
     uses to decide which gift is actually put in the cart. Identical input plus
     identical rule is what makes "the bar cannot promise a gift the engine will
     not grant" provable rather than hoped for (task-5795-tests.mjs group C).

     null means REFUSE TO DECIDE (unknown currency, a currency the page and the
     Shopify global disagree about, an unusable rate). Callers then paint nothing
     instead of a euro number — there is deliberately no EUR fallback. */
  function cartEurFromCents(cents) {
    if (window.ILSMoney && window.ILSMoney.on()) return window.ILSMoney.eurFromCents(cents);
    return (Number(cents) || 0) / 100;
  }

  function getRwConfig() {
    if (getRwConfig._cache !== undefined) return getRwConfig._cache;
    var cfg = { country: '', thresholds: {} };
    try {
      var tag = document.querySelector('script[data-ils-rw-config]');
      if (tag) {
        var parsed = JSON.parse((tag.textContent || '').trim() || '{}');
        if (parsed && typeof parsed === 'object') {
          if (typeof parsed.country === 'string') cfg.country = parsed.country.toUpperCase();
          if (parsed.thresholds && typeof parsed.thresholds === 'object') cfg.thresholds = parsed.thresholds;
        }
      }
    } catch (e) {}
    getRwConfig._cache = cfg;
    return cfg;
  }

  /* TASK-3300/3311 — the free-shipping threshold's SINGLE SOURCE OF TRUTH is the
     ILS Delivery admin (production ils-admin), NOT Shopify Delivery Profiles.
     Each country has several delivery methods (courier + lockers), each with its
     own free-shipping threshold (e.g. LV: courier 80 / locker 35). The cart bar
     shows the LOWEST threshold for the country, because the shopper can pick any
     method and unlocks free shipping at the cheaper bar (owner directive). We
     read it live from GET {PROD}/api/delivery/rates?country=XX and use it as the
     primary value; the shop-metafield map (custom.shipping_thresholds) is kept
     ONLY as an instant no-flash fallback for the first paint / offline. */
  var ILS_DELIVERY_API = 'https://alert-dedication-production.up.railway.app';
  var apiShipThreshold = null;   // resolved number once the live API answers
  var apiShipFetched = false;
  /* TASK-5954 — the method the minimum belongs to, so the bar can NAME it.
     The bar shows the LOWEST threshold for the country (owner directive,
     TASK-3300/3311), and on LV/LT/EE that is the parcel locker at EUR 35
     while the courier is free only from EUR 80. Printing 35 with no method
     reads as a free-COURIER promise across the whole 35-79.99 range, which
     is REPORT-5951 section 6 item 8.

     The rates payload already carries each rate's `type` and the
     `free_shipping` map is keyed by method, so the owning method is OBSERVED,
     never guessed. It is set only when another method has a strictly HIGHER
     threshold — i.e. only where the distinction is real — so every market
     whose methods share one threshold (DE, the EUR-100 zone, CY/MT) keeps
     `null` and renders exactly as before. */
  var apiShipMethod = null;      /* 'locker' | 'courier' | null */
  function minThresholdFromRates(data) {
    if (!data || typeof data !== 'object') return null;
    var vals = [];
    var byType = {};
    var fs = data.free_shipping;
    if (fs && typeof fs === 'object') {
      for (var k in fs) {
        var n = Number(fs[k]);
        if (!isFinite(n) || n <= 0) continue;
        vals.push(n);
        if (byType[k] == null || n < byType[k]) byType[k] = n;
      }
    }
    if (Array.isArray(data.rates)) {
      data.rates.forEach(function (r) {
        var n2 = Number(r && r.free_shipping_threshold_eur);
        if (!isFinite(n2) || n2 <= 0) return;
        vals.push(n2);
        var ty = (r && r.type) ? String(r.type).toLowerCase() : null;
        if (ty && (byType[ty] == null || n2 < byType[ty])) byType[ty] = n2;
      });
    }
    if (!vals.length) {
      var t = Number(data.free_shipping_threshold_eur);
      if (isFinite(t) && t > 0) vals.push(t);
    }
    if (!vals.length) { apiShipMethod = null; return null; }
    var min = Math.min.apply(null, vals);
    var owner = null, higherExists = false;
    for (var ty2 in byType) {
      if (byType[ty2] === min && (ty2 === 'locker' || ty2 === 'courier')) owner = ty2;
      if (byType[ty2] > min) higherExists = true;
    }
    apiShipMethod = (owner && higherExists) ? owner : null;
    return min;
  }
  function fetchApiShipThreshold() {
    if (apiShipFetched) return;
    apiShipFetched = true;
    var cfg = getRwConfig();
    if (!cfg.country) return;
    fetch(ILS_DELIVERY_API + '/api/delivery/rates?country=' + encodeURIComponent(cfg.country), { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (data) {
        var m = minThresholdFromRates(data);
        if (m != null) {
          apiShipThreshold = m;
          renderRewards(cartEurFromCents(lastKnownCartCents));   // TASK-5795: repaint in EUR terms
        }
      })
      .catch(function () { /* keep the metafield fallback on any error */ });
  }

  function getShipThreshold() {
    /* TASK-5795 — CONTAINMENT, six markets only.
       Both sources below publish the threshold in EUR (the live API field is
       literally named free_shipping_threshold_eur). On an EUR market that is a
       number we can state truthfully. On a local-currency market it is not:
       TASK-5784 §1.3 measured that Shopify evaluates the EUR rate condition at
       the UN-marked-up reference rate while Shopify.currency.rate carries the
       ~+2 % conversion markup, so the real trigger is not reachable from any
       number this page can read, and it moves daily. TASK-5793 then proved the
       gap at a REAL checkout: a Swedish cart at the advertised 1100 SEK is
       charged 148 SEK for courier.
       Returning null is an EXISTING, well-trodden code path (TASK-2399): the
       bar is hidden entirely and never re-appears. We withhold the claim rather
       than restate it wrongly — publishing an exact local threshold needs the
       Checkout Function that WORK-0132 is blocked on. */
    if (window.ILSMoney && window.ILSMoney.on() && !window.ILSMoney.mayClaimFreeShipping()) return null;
    /* TASK-5856 — on a fixed-local market the threshold comes from the APPROVED
       MATRIX and from nowhere else, and it is returned on the EUR axis this
       function's callers already compare on.

       That is not a converted threshold for display: nothing printed comes from
       here (shipThresholdText() below prints the approved local amount itself).
       The cart and this number are divided by the SAME rate, so
       `cartEur >= this` is algebraically `cartPresentmentMajor >= 1100 SEK`,
       which is the test the live Shopify Function applies. One line instead of a
       second currency axis through every bar.

       It also deliberately SHADOWS both legacy sources for these six. The live
       API publishes free_shipping_threshold_eur = null for all of them
       (TASK-5830 set country_free_shipping to NULL) and the shop metafield
       custom.shipping_thresholds still carries the stale EUR 100 for all six —
       REPORT-5848 D11. Reading either one here is how a EUR-100 promise would
       come back on a market whose real threshold is PLN 450. */
    var _R5856 = window.ILSRewardMatrix;
    if (_R5856 && typeof _R5856.freeShipLocalEur === 'function' && _R5856.isFixedLocal()) {
      var _fl5856 = _R5856.freeShipLocalEur();
      return (typeof _fl5856 === 'number' && _fl5856 > 0) ? _fl5856 : null;
    }
    if (typeof apiShipThreshold === 'number' && apiShipThreshold > 0) return apiShipThreshold;
    var cfg = getRwConfig();
    if (!cfg.country) return null;
    var t = cfg.thresholds && cfg.thresholds[cfg.country];
    return (typeof t === 'number' && t > 0) ? t : null;
  }

  /* Given the cart euro total and the (possibly null) ship threshold, decide
     which of the 3 bars should be visible.

     TASK-3293 — "only the nearest incentive" rule, REPLACING the TASK-2400
     "sticky after unlock" rule. Owner directive: at any cart total show ONLY
     the bar(s) whose NEAREST still-unreached threshold equals the minimum
     nearest-unreached threshold across all three bars. Ties show several bars
     together (owner example: at €60 Mango-next €80 and Giveaway-first €80 tie
     → both bars show). A partially-unlocked bar NO LONGER "sticks": at €80 the
     Mango bar (next €100) is hidden because Giveaway's next €90 is nearer.
     Removing the old `engaged` clause is exactly the fix for the TASK-3292
     complaint that two bars showed at once through most of the range instead
     of only the closest incentive.

     A fully maxed-out bar has no unreached threshold (nextAt === null): it does
     not take part in the minimum comparison, but is still shown in its final
     "Max ✓" state so the reward-achieved celebration is preserved (owner:
     "либо показывает финальное состояние"). If the ship threshold is null
     (country has no free shipping) the ship bar is null → hidden and excluded
     from the comparison entirely.

     TASK-4855 — the ship (delivery / free-shipping) bar is now EXEMPT from
     this competition: it is ALWAYS visible whenever the country has a free
     shipping threshold at all. Under the pure TASK-3293 rule a nearer gift /
     giveaway tier could out-rank it and hide the delivery progress entirely,
     which is the bug reported by TASK-4851. `minNext` is deliberately still
     computed over all three bars, so mango and give keep the exact visibility
     they have today — the exemption only ever ADDS the ship row, it never
     changes which of the other two bars wins.

     Cart totals arrive as cents/100; compare thresholds with a half-cent
     tolerance (rwReached) so float drift never leaves an exactly-reached tier
     "unreached". nextAt values come straight from the integer tier tables, so
     the `nextAt === minNext` equality is an exact integer comparison. */
  /* TASK-5795 — the tolerance is now ZERO, so this comparison is the gift
     engine's comparison (assets/custom-gift-logic.js targetGramsForTotal uses a
     bare `>=`). Two reasons:
       1. CORRECTNESS. 0.005 EUR is worth ~1.87 HUF — WIDER than one minor unit.
          A Hungarian cart one forint below a tier was told it had reached it
          while the engine granted nothing. On EUR markets the window was
          narrower than a cent and so unreachable; after conversion it is not.
       2. NO EUR REGRESSION, provably. The rules differ only for cart totals in
          [100*at - 0.5, 100*at) cents. Every tier is an integer, so that
          half-open interval contains no integer, and cart totals are always
          integer minor units. scripts/task-5795-tests.mjs group G asserts this
          exhaustively over every tier and an 800-cent window around each. */
  var RW_EPS = 0;
  function rwReached(cartEur, at) { return cartEur >= at - RW_EPS; }
  function computeRwVisibility(cartEur, shipThreshold) {
    syncTierAxis();                       /* TASK-5954 — before any compare */
    var bars = { ship: null, mango: null, give: null };
    if (shipThreshold != null) {
      var shipDone = rwReached(cartEur, shipThreshold);
      /* Ship has a single tier — reached ⇒ final; otherwise its only
         unreached threshold is the threshold itself. */
      bars.ship = { isFinal: shipDone, nextAt: shipDone ? null : shipThreshold };
    }
    var mangoLast = MANGO[MANGO.length - 1].at;
    var mangoDone = rwReached(cartEur, mangoLast);
    var mangoNext = null;
    for (var i = 0; i < MANGO.length; i++) { if (!rwReached(cartEur, MANGO[i].at)) { mangoNext = MANGO[i].at; break; } }
    bars.mango = { isFinal: mangoDone, nextAt: mangoNext };
    var giveLast = GIVE[GIVE.length - 1].at;
    var giveDone = rwReached(cartEur, giveLast);
    var giveNext = null;
    for (var j = 0; j < GIVE.length; j++) { if (!rwReached(cartEur, GIVE[j].at)) { giveNext = GIVE[j].at; break; } }
    bars.give = { isFinal: giveDone, nextAt: giveNext };

    var minNext = null;
    for (var k in bars) {
      if (!bars[k]) continue;
      var na = bars[k].nextAt;
      if (na != null && (minNext == null || na < minNext)) minNext = na;
    }
    var visible = { ship: false, mango: false, give: false };
    for (var m in bars) {
      var b = bars[m];
      if (!b) { visible[m] = false; continue; }
      /* TASK-4855 — delivery / free shipping is exempt from the "nearest
         incentive" rule and is ALWAYS shown. Not reached → the row keeps the
         unchanged progress bar + remaining amount; reached → the row keeps the
         unchanged "Free shipping ✓" state (which already drops the remaining
         amount). A country with no free-shipping threshold leaves bars.ship
         null and is still hidden by the `if (!b)` line above. */
      if (m === 'ship') { visible[m] = true; continue; }
      if (b.isFinal) visible[m] = true;                 // maxed → keep the "Max ✓" celebration visible
      else if (b.nextAt === minNext) visible[m] = true; // nearest still-unreached incentive
      else visible[m] = false;
    }
    return visible;
  }

  /* TASK-4846 — ONE global money format on EVERY published locale,
     English included: decimal separator ',' and thousands separator '.'
     (€0,00 / €89,90 / €1.234,56). Deliberately not Intl.NumberFormat or
     toLocaleString: this is a storefront business rule, not a locale
     convention, and en-* would render €1,234.56. Same algorithm as
     moneyFromCents() in custom-cart-drawer.js. Presentation only — the
     numeric value and its rounding are untouched. */
  function money2(n) {
    const v = isFinite(Number(n)) ? Number(n) : 0;
    const sign = v < 0 ? '-' : '';
    const cents = Math.round(Math.abs(v) * 100);
    /* TASK-5946 — display only: a FULLY zero fraction prints as no fraction. */
    const frac = String(cents % 100).padStart(2, '0');
    return sign + String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.') +
      (frac === '00' ? '' : ',' + frac);
  }
  /* TASK-5856 — the three money helpers the reward panel prints through.

     rwMoney(eur)   an amount on the EUR reward ladder that the shopper is being
                    asked to REACH: a tier tick, an "add X more" delta. Rounded
                    UP to the currency's granularity by the matrix, because the
                    gift engine grants at the EUR tier and a number that rounded
                    down could be hit without qualifying.
     rwFact(eur)    an EUR amount that is a reported fact, not a target — the
                    weekly leader's basket, the shopper's own subtotal. Nobody
                    is asked to reach it, so it is not pushed up a grid.
     shipMoney()    the free-courier threshold. A LOOKUP of the approved fixed
                    local amount, never a conversion, which is the whole point
                    of the matrix.

     All three fall back to the exact pre-TASK-5856 euro string whenever the
     oracle refuses or the market is EUR, so EUR markets keep today's bytes. */
  /* TASK-5954 — rwMoney() is now ONLY the euro fallback formatter the three
     helpers below share. Nothing calls it directly for a threshold any more:
     a tier goes through rwTier() (a table read) and a gap through rwDelta()
     (a converted delta). Keeping the name means the EUR output of every
     surface is still produced by exactly one function. */
  function rwMoney(eur) {
    var R = window.ILSRewardMatrix;
    if (R && typeof R.eurMoneyUp === 'function') {
      try { var s = R.eurMoneyUp(eur); if (s !== null) return s; } catch (e) {}
    }
    return '€' + money2(eur);
  }

  /* A REWARD TIER LABEL — the owner-approved amount for this market, read
     from the matrix by the tier's EUR identity. This is the line REPORT-5951
     was about: it used to print ceil(tierEur * Shopify.currency.rate), e.g.
     "1 128 kr" for the rung the owner approved at 1 100 kr.
     `row` may be a tier object or a bare EUR number. */
  function rwTier(row) {
    var eur = (row && typeof row === 'object') ? row.eur : row;
    var R = window.ILSRewardMatrix;
    if (R && typeof R.tierMoney === 'function') {
      try { var s = R.tierMoney(eur); if (s !== null) return s; } catch (e) {}
    }
    return '€' + money2(eur);
  }

  /* A GAP — "add X more". Both operands are already on the current market's
     axis, so this converts the difference ONCE and rounds it up to one whole
     display unit. Deliberately not eurMoneyUp(): that ceils to the
     GRANULARITY grid, which on HUF would overstate a gap by up to 99 Ft now
     that the threshold itself is exact. */
  function rwDelta(eurDelta) {
    var R = window.ILSRewardMatrix;
    if (R && typeof R.deltaMoney === 'function') {
      try { var s = R.deltaMoney(eurDelta); if (s !== null) return s; } catch (e) {}
    }
    return '€' + money2(eurDelta);
  }
  function rwFact(eur) {
    var R = window.ILSRewardMatrix;
    if (R && typeof R.eurMoney === 'function') {
      try { var s = R.eurMoney(eur); if (s !== null) return s; } catch (e) {}
    }
    return '€' + money2(eur);
  }
  /* TASK-5954 — the method the printed threshold actually buys, as an
     ALREADY-TRANSLATED suffix, or '' when the country's methods agree.

     The labels come from the delivery selector's own locale keys
     (custom.delivery_selector.locker / .courier), which are published as
     market overrides on this very theme for every live locale — lv
     "Pakomats"/"Kurjers", lt "Pastomatas"/"Kurjeris", et
     "Pakiautomaat"/"Kuller", de "Paketautomat"/"Kurier", ru, en. So nothing
     new is translated here and the suffix can never fall back to English on
     a translated market.

     NEVER applied on a fixed-local market: there the approved amount IS the
     free-COURIER amount — that is what TASK-5852 measured at a real
     Checkout — so naming a method would be wrong rather than clearer. */
  function shipMethodSuffix() {
    if (!apiShipMethod) return '';
    var i18 = rwI18n();
    var label = apiShipMethod === 'locker' ? i18.methodLocker : i18.methodCourier;
    if (!label || typeof label !== 'string') return '';
    return ' \u00b7 ' + label;
  }
  function shipMoney(thresholdEur) {
    var R = window.ILSRewardMatrix;
    if (R && typeof R.freeShipLocalMoney === 'function' && R.isFixedLocal()) {
      try { var s = R.freeShipLocalMoney(); if (s !== null) return s; } catch (e) {}
    }
    /* The EUR path: whole euros still print whole ("€100"), now followed
       by the method when the country's methods disagree ("€35 · Pakomats"). */
    return '€' + (Math.round(Number(thresholdEur) * 100) % 100 === 0
      ? String(Math.round(Number(thresholdEur))).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      : money2(thresholdEur)) + shipMethodSuffix();
  }
  function posForIndex(i, n) { return n === 1 ? 100 : 8 + (i * (84 / (n - 1))); }

  function buildTicks(el, list) {
    if (!el) return;
    el.innerHTML = '';
    const n = list.length;
    list.forEach((m, i) => {
      const t = document.createElement('div');
      t.className = 'tick';
      t.style.left = posForIndex(i, n) + '%';
      /* TASK-5856 — REPORT-5848 D7: the ticks were hardcoded '€' + m.at, so a
         Swedish cart read "€60 / €80 / €100 / €150 / €200".
         TASK-5954 — and then they read the CONVERTED tier ("1 128 kr" for the
         rung approved at 1 100 kr). Now they read the approved amount itself,
         by table lookup. */
      t.innerHTML = '<span class="stem"></span><span class="amt">' + rwTier(m) + '</span><span class="prize">' + m.prize + '</span>';
      el.appendChild(t);
    });
  }
  function fillFor(list, cart) {
    const n = list.length;
    let reached = -1;
    for (let i = 0; i < n; i++) { if (cart >= list[i].at) reached = i; }
    if (reached === n - 1) return posForIndex(n - 1, n);
    if (reached === -1) {
      const f = cart / list[0].at;
      return posForIndex(0, n) * Math.max(0, Math.min(1, f));
    }
    const lo = list[reached].at, hi = list[reached + 1].at, fr = (cart - lo) / (hi - lo);
    return posForIndex(reached, n) + fr * (posForIndex(reached + 1, n) - posForIndex(reached, n));
  }
  function stateFor(list, cart) {
    const first = list[0].at, last = list[list.length - 1].at;
    if (cart >= last) return 'green';
    if (cart >= first) return 'amber';
    return 'red';
  }
  function paintTicks(el, list, cart) {
    if (!el) return;
    /* TASK-5954 — buildTicks() runs once, lazily, and may fire before
       ILSMoney has resolved the market (the format is derived from a JSON
       block, and this asset is deferred). Re-stamping the amount on every
       repaint means a tick can never be left showing the euro fallback on a
       market whose oracle arrived a tick later. Idempotent: writing the same
       string back is a no-op. */
    var amts = el.querySelectorAll('.tick .amt');
    for (var a = 0; a < amts.length && a < list.length; a++) {
      var want = rwTier(list[a]);
      if (amts[a].textContent !== want) amts[a].textContent = want;
    }
    let nextIdx = -1;
    for (let j = 0; j < list.length; j++) { if (cart < list[j].at) { nextIdx = j; break; } }
    const ts = el.querySelectorAll('.tick');
    list.forEach((m, i) => {
      if (!ts[i]) return;
      ts[i].classList.remove('done', 'next');
      if (cart >= m.at) ts[i].classList.add('done');
      else if (i === nextIdx) ts[i].classList.add('next');
    });
  }

  const FILL_COLORS = { red: '#C60404', amber: '#F2B705', green: '#2BB34B' };
  function paintFill(el, widthPct, st) {
    if (!el) return;
    const w = Math.max(0, Math.min(100, widthPct));
    el.style.width = w + '%';
    el.style.height = '100%';
    el.style.background = FILL_COLORS[st] || FILL_COLORS.red;
    el.style.backgroundColor = FILL_COLORS[st] || FILL_COLORS.red;
    el.style.display = 'block';
    el.className = 'fill s-' + st;
  }

  function setBarMax(rowEl, isMax) {
    if (!rowEl) return;
    rowEl.classList.toggle('is-max', !!isMax);
  }

  const HEART_SVG = '<span class="hb" aria-hidden="true">'
    + '<svg viewBox="0 0 188.1213 188.1213" xmlns="http://www.w3.org/2000/svg">'
    + '<path transform="translate(-1389.3353,-1889.2884)" '
    + 'd="M1483.396,1929.109c-23.006-49.847-98.417-24.924-93.863,29.317,4.465,53.191,60.632,60.152,93.863,106.095,33.232-45.943,89.398-52.905,93.863-106.095,4.553-54.241-70.857-79.165-93.863-29.317Z" '
    + 'fill="#C60404"/></svg></span>';

  /* TASK-2560 — localized reward/leader strings. The bar labels are already
     Liquid `| t` (custom.cart.free_shipping / mango_gift / giveaway_chances);
     the state/goal/leader text below used to be hardcoded English. Read the
     translated templates emitted by snippets/custom-cart-rewards.liquid
     (data-ils-rw-i18n) and fall back to these English defaults if the block is
     absent or unparseable — zero regression. Placeholders in {curly} braces are
     filled by rwFmt() at render time. */
  var _rwI18nCache = null;
  function rwI18n() {
    if (_rwI18nCache) return _rwI18nCache;
    var d = {
      shipDone: 'Free shipping',
      /* TASK-5954 — defaults for the two method labels. Used only in the
         window before the Liquid i18n block below is parsed; the block
         overrides them with the market's own published translation. */
      methodLocker: 'Parcel locker',
      methodCourier: 'Courier',
      shipUnlocked: 'Free shipping unlocked ✓ (from €{amount})',
      shipAdd: 'Add <b>€{amount}</b> for free shipping (€{threshold})',
      mangoMaxState: '{weight} Max',
      mangoMax: 'Max gift: <b>{weight}</b> ✓',
      mangoNone: 'none',
      mangoAdd: 'Add <b>€{amount}</b> → mango <b>{weight}</b>',
      giveMaxState: '{prize} Max',
      giveMax: 'Max chances: <b>{prize}</b> ✓',
      giveNotEntered: 'not entered',
      giveAddEnter: 'Add <b>€{amount}</b> → enter giveaway (<b>×1</b>)',
      giveAddChance: 'Add <b>€{amount}</b> → chance <b>{prize}</b>',
      leaderLabel: 'This Week Current Leader:',
      leaderLabelYou: 'YOU are This Week Current Leader:',
      selectWeight: 'Select weight',
      leaderStim: 'Just <b>€{amount}</b> more to become this week\'s leader!',
      leaderWinTitle: 'You\'re this week\'s leader!',
      leaderWinSub: 'Place this order – if no one outspends you by Sunday, you\'ll earn a <b>€20 bonus</b> on your next order!'
    };
    var found = false;
    try {
      var el = document.querySelector('[data-ils-rw-i18n]');
      if (el && el.textContent) {
        var p = JSON.parse(el.textContent);
        for (var k in p) { if (p[k]) { d[k] = p[k]; } }
        found = true;
      }
    } catch (e) { /* keep English defaults */ }
    /* Only cache once we actually read the emitted block, so a call before the
       snippet is in the DOM does not lock in the English fallback. */
    if (found) { _rwI18nCache = d; }
    return d;
  }
  function rwFmt(tpl, vars) {
    /* TASK-5856 — the reward templates hard-code "€" INSIDE the translated
       sentence ("Add <b>€{amount}</b> → mango <b>{weight}</b>" in en.default and
       its equivalents in ru), so substituting an already-formatted presentment
       string produced "€531,00 kr". ILSMoney.dropSign() was written for exactly
       this in TASK-5795 and had ZERO call sites on the live theme (REPORT-5848
       D6) — this is the call site. It is purely subtractive: a template with no
       sign adjacent to the placeholder comes back untouched, so every EUR market
       and every untranslated locale renders byte-identically to before. */
    var t = String(tpl);
    if (window.ILSMoney && window.ILSMoney.on() && typeof window.ILSMoney.dropSign === 'function') {
      try { t = window.ILSMoney.dropSign(t); } catch (e) { t = String(tpl); }
    }
    return t.replace(/\{(\w+)\}/g, function (m, key) {
      return (vars && vars[key] != null) ? vars[key] : '';
    });
  }

  function renderRewardsPanel(panel, cartEur, isLeading) {
    if (!panel) return;

    /* TASK-2399 — dynamic threshold + nearest-goal visibility. Compute up
       front so every bar's render path can consult the same decision, and
       so we can hide rows completely (row.hidden = true → display:none) with
       zero residual gap between visible bars. */
    syncTierAxis();                       /* TASK-5954 — before any compare */
    var shipThreshold = getShipThreshold();
    var visible = computeRwVisibility(cartEur, shipThreshold);

    const shipRow = panel.querySelector('[data-rw-row="ship"]');
    const shipFill = panel.querySelector('[data-rw-fill="ship"]');
    const shipState = panel.querySelector('[data-rw-state="ship"]');
    const shipGoal = panel.querySelector('[data-rw-goal="ship"]');
    if (shipRow) shipRow.hidden = !visible.ship;
    if (shipThreshold != null && shipFill && shipState && shipGoal) {
      const done = cartEur >= shipThreshold;
      const st = done ? 'green' : (cartEur >= shipThreshold * 0.5 ? 'amber' : 'red');
      const w = done ? 100 : Math.min(100, (cartEur / shipThreshold) * 100);
      paintFill(shipFill, w, st);
      shipState.className = 'pbar-state s-' + st + (done ? ' is-max' : '');
      /* TASK-5856 — this row is the feature the containment was hiding. On the
         six it now states the APPROVED LOCAL amount (a matrix lookup, e.g.
         "1 100 kr") and the remaining delta in the same currency. On EUR markets
         shipMoney()/rwMoney() both fall back to the previous euro strings, so
         those markets render exactly as before. */
      var shipTxt = shipMoney(shipThreshold);
      if (done) {
        shipState.innerHTML = rwI18n().shipDone + ' ' + HEART_SVG;
        shipGoal.className = 'goal done';
        shipGoal.innerHTML = rwFmt(rwI18n().shipUnlocked, { amount: shipTxt });
      } else {
        shipState.textContent = '+' + rwDelta(shipThreshold - cartEur);
        shipGoal.className = 'goal';
        shipGoal.innerHTML = rwFmt(rwI18n().shipAdd, { amount: rwDelta(shipThreshold - cartEur), threshold: shipTxt });
      }
      setBarMax(shipRow, done);
    }

    const mangoRow = panel.querySelector('[data-rw-row="mango"]');
    const mangoFill = panel.querySelector('[data-rw-fill="mango"]');
    const mangoState = panel.querySelector('[data-rw-state="mango"]');
    const mangoGoal = panel.querySelector('[data-rw-goal="mango"]');
    const mangoTicks = panel.querySelector('[data-rw-ticks="mango"]');
    if (mangoRow) mangoRow.hidden = !visible.mango;
    if (mangoFill && mangoState && mangoGoal) {
      const st = stateFor(MANGO, cartEur);
      /* TASK-2400 — read final-tier threshold + prize from the MANGO table
         (last entry) instead of hardcoding 200 / '1000g'. Keeps this block
         in sync automatically if the tier ladder is edited above. */
      const mangoLastAt = MANGO[MANGO.length - 1].at;
      const mangoLastPrize = MANGO[MANGO.length - 1].prize;
      const maxMango = cartEur >= mangoLastAt;
      const mangoFull = cartEur > mangoLastAt && isLeading;
      paintFill(mangoFill, mangoFull ? 100 : fillFor(MANGO, cartEur), maxMango ? 'green' : st);
      mangoState.className = 'pbar-state s-' + st + (maxMango ? ' is-max' : '');
      if (maxMango) {
        mangoState.innerHTML = rwFmt(rwI18n().mangoMaxState, { weight: formatWeight(mangoLastPrize) }) + ' ' + HEART_SVG;
        mangoGoal.className = 'goal done';
        mangoGoal.innerHTML = rwFmt(rwI18n().mangoMax, { weight: formatWeight(mangoLastPrize) });
      } else {
        let nxt = null;
        for (let i = 0; i < MANGO.length; i++) { if (cartEur < MANGO[i].at) { nxt = MANGO[i]; break; } }
        /* TASK-5954 — was a hardcoded `cartEur >= 60`, which is the EUR first
           rung. On SEK the first approved rung is 650 kr, so the hardcode
           announced "125 g" from EUR 60 (= 676 kr) instead of from 650 kr.
           MANGO[0].at is that same rung on the live axis. */
        if (cartEur >= MANGO[0].at) {
          const cur = MANGO.filter((m) => cartEur >= m.at).pop() || MANGO[0];
          mangoState.textContent = formatWeight(cur.prize);
        } else {
          mangoState.textContent = rwI18n().mangoNone;
        }
        mangoGoal.className = 'goal';
        const mn = nxt || MANGO[MANGO.length - 1];
        mangoGoal.innerHTML = rwFmt(rwI18n().mangoAdd, { amount: rwDelta(mn.at - cartEur), weight: formatWeight(mn.prize) });
      }
      paintTicks(mangoTicks, MANGO, cartEur);
      setBarMax(mangoRow, maxMango);
    }

    const giveRow = panel.querySelector('[data-rw-row="give"]');
    const giveFill = panel.querySelector('[data-rw-fill="give"]');
    const giveState = panel.querySelector('[data-rw-state="give"]');
    const giveGoal = panel.querySelector('[data-rw-goal="give"]');
    const giveTicks = panel.querySelector('[data-rw-ticks="give"]');
    if (giveRow) giveRow.hidden = !visible.give;
    if (giveFill && giveState && giveGoal) {
      const st = stateFor(GIVE, cartEur);
      /* TASK-2400 — dynamic final tier for Giveaway (same reasoning as Mango
         above): last GIVE entry drives both the "max reached" threshold and
         the label shown ("×5 Max"). */
      const giveLastAt = GIVE[GIVE.length - 1].at;
      const giveLastPrize = GIVE[GIVE.length - 1].prize;
      const maxGive = cartEur >= giveLastAt;
      const giveFull = cartEur > giveLastAt && isLeading;
      paintFill(giveFill, giveFull ? 100 : fillFor(GIVE, cartEur), maxGive ? 'green' : st);
      giveState.className = 'pbar-state s-' + st + (maxGive ? ' is-max' : '');
      if (maxGive) {
        giveState.innerHTML = rwFmt(rwI18n().giveMaxState, { prize: giveLastPrize }) + ' ' + HEART_SVG;
        giveGoal.className = 'goal done';
        giveGoal.innerHTML = rwFmt(rwI18n().giveMax, { prize: giveLastPrize });
      } else {
        let nxt = null;
        for (let j = 0; j < GIVE.length; j++) { if (cartEur < GIVE[j].at) { nxt = GIVE[j]; break; } }
        /* TASK-5954 — these two tests were a hardcoded EUR 80, the EUR entry
           rung. The approved SEK entry rung is 900 kr, which on the EUR axis
           is ~79.86 — BELOW 80. So a Swedish cart holding exactly the
           approved 900 kr was told it had not entered the giveaway at all,
           and a Czech cart one haler under its approved 2000 Kc landed in the
           window where `cartEur >= 80` was true but no rung was reached, so
           `GIVE.filter(...).pop()` was undefined and reading `.prize` threw —
           which kills the whole panel repaint, not just this bar.
           GIVE[0].at is that same entry rung on the live axis.
           `cur`/`nxt` are also made total: the ladder is ordered, so a
           float edge can fall between two rungs, and a thrown exception here
           blanks three bars. Falling back to the nearest rung degrades to a
           correct-by-one-rung label instead of no panel. */
        const giveEntryAt = GIVE[0].at;
        if (cartEur >= giveEntryAt) {
          const cur = GIVE.filter((m) => cartEur >= m.at).pop() || GIVE[0];
          giveState.textContent = cur.prize;
        } else {
          giveState.textContent = rwI18n().giveNotEntered;
        }
        giveGoal.className = 'goal';
        if (cartEur < giveEntryAt) {
          giveGoal.innerHTML = rwFmt(rwI18n().giveAddEnter, { amount: rwDelta(giveEntryAt - cartEur) });
        } else {
          const gn = nxt || GIVE[GIVE.length - 1];
          giveGoal.innerHTML = rwFmt(rwI18n().giveAddChance, { amount: rwDelta(gn.at - cartEur), prize: gn.prize });
        }
      }
      paintTicks(giveTicks, GIVE, cartEur);
      setBarMax(giveRow, maxGive);
    }
  }

  function renderRewards(cartEur) {
    const panels = document.querySelectorAll('[data-ils-rewards]');
    /* TASK-5795 — the PDP's "in cart" figure is the shopper's own money, not an
       EUR equivalent, so it is printed from the presentment cents directly
       instead of being reconstructed from the converted EUR value. */
    document.querySelectorAll('[data-ils-pdp-cart]').forEach((el) => {
      el.textContent = moneyFromCents(
        (window.ILSMoney && window.ILSMoney.on()) ? lastKnownCartCents : ((cartEur * 100) | 0)
      );
    });
    /* TASK-5795 — cartEur === null means the conversion source was unusable, so
       we do not know the shopper's EUR subtotal. Every reward row is then
       hidden: an unknown position on an EUR ladder must show nothing, never a
       guess. The leader row keeps its live value, which is EUR already. */
    if (cartEur === null) {
      panels.forEach((p) => {
        ['ship', 'mango', 'give'].forEach((n) => {
          const row = p.querySelector('[data-rw-row="' + n + '"]');
          if (row) row.hidden = true;
        });
      });
      document.querySelectorAll('[data-ils-cd-stim], [data-ils-cd-pop]').forEach((el) => { el.hidden = true; el.innerHTML = ''; });
      return;
    }
    const leaderEurForFill = getLeaderEur();
    const isLeading = leaderEurForFill > 0 && cartEur > leaderEurForFill;
    panels.forEach((p) => renderRewardsPanel(p, cartEur, isLeading));
    /* TASK-2101 — sole call site for renderLeaderState() during a normal
       rewards repaint. See updateRewardsFromTotalCents() below. */
    renderLeaderState(cartEur);
  }

  let lastKnownCartCents = 0;

  function initRewards() {
    const panels = document.querySelectorAll('[data-ils-rewards]');
    if (!panels.length) return;
    panels.forEach((panel) => {
      const mangoTicks = panel.querySelector('[data-rw-ticks="mango"]');
      const giveTicks = panel.querySelector('[data-rw-ticks="give"]');
      if (mangoTicks && !mangoTicks.querySelector('.tick')) buildTicks(mangoTicks, MANGO);
      if (giveTicks && !giveTicks.querySelector('.tick')) buildTicks(giveTicks, GIVE);
    });
    let cents = NaN;
    panels.forEach((panel) => {
      const v = parseInt(panel.dataset.cartTotal || '', 10);
      if (Number.isFinite(v) && !Number.isFinite(cents)) cents = v;
    });
    if (!Number.isFinite(cents)) cents = lastKnownCartCents;
    else lastKnownCartCents = cents;
    renderRewards(cartEurFromCents(cents));
    fetchApiShipThreshold();   // TASK-3300/3311 — live per-country threshold (once), then repaint
  }

  function updateRewardsFromTotalCents(cents) {
    lastKnownCartCents = cents | 0;
    /* TASK-2101 — removed a duplicate renderLeaderState() call that used
       to run here directly, BEFORE initRewards()/renderRewards() ran it
       again. Two synchronous calls per cart-total update raced against
       the sessionStorage confetti flag and could re-fire confetti even
       when the cart was already leading with no drop below threshold.
       initRewards() → renderRewards() → renderLeaderState() is now the
       only path. */
    const panels = document.querySelectorAll('[data-ils-rewards]');
    if (!panels.length) return;
    panels.forEach((p) => { p.dataset.cartTotal = String(lastKnownCartCents); });
    initRewards();
  }

  /* TASK-2674 — wake custom-gift-logic.js's gift reconciler after a DRAWER-side
     cart mutation. The drawer's own AJAX paths (cartChangeByKey: remove / qty /
     variant-swap; wireUpsellAdd: add) update the drawer inline but never emitted
     the cart-change signal the reconciler is subscribed to. So removing the last
     product via the DRAWER (mini-cart) left the free gift stranded — unremovable
     until a page reload — which was the owner's live bug: the reconciler was
     never told the cart changed. The /cart PAGE already dispatched this event
     (custom-cart-page.js refreshDrawerSection), which is exactly why removal
     there worked and ONLY the drawer path was silent. We dispatch the same
     lightweight 'cart:update' event; the reconciler re-reads /cart.js and
     adds / upgrades / removes the gift as the subtotal crosses thresholds.
     This wakes only the reconciler (+ the drawer's own light count refresh) —
     it does NOT publish PUB_SUB cartUpdate, so the heavier upsell/leader reload
     is not re-run on every stepper click. No loop: the reconciler's own gift
     mutations publish PUB_SUB cartUpdate, they never dispatch 'cart:update'. */
  function notifyGiftReconciler() {
    try { document.dispatchEvent(new CustomEvent('cart:update')); } catch (e) {}
  }

  let pendingGiftSync = null;
  function scheduleGiftSync() {
    notifyGiftReconciler();
    if (pendingGiftSync) clearTimeout(pendingGiftSync);
    pendingGiftSync = setTimeout(function () {
      pendingGiftSync = null;
      /* TASK-2962 — locale-prefix the drawer section re-render (empty-state text
         leaked German on non-default markets when fetched naked). */
      fetch(localeRoot() + '/cart?section_id=cart-drawer', { headers: { Accept: 'text/html' } })
        .then((r) => r.ok ? r.text() : '')
        .then((html) => { if (html) morphCartDrawer(html); })
        .catch(() => {});
      fetchAndUpdateCartCount();
    }, 350);
  }

  function clearDawnCartErrors() {
    const drawerEl = document.querySelector('cart-drawer');
    if (!drawerEl) return;
    drawerEl.querySelectorAll('#CartDrawer-CartErrors, .cart-errors').forEach((el) => { el.textContent = ''; });
    drawerEl.querySelectorAll('.cart-item__error-text').forEach((el) => { el.textContent = ''; });
  }

  function silentRecover() {
    /* TASK-2962 — locale-prefix the recovery re-render (same leak class). */
    return fetch(localeRoot() + '/cart?section_id=cart-drawer', { headers: { Accept: 'text/html' } })
      .then((r) => r.ok ? r.text() : '')
      .then((html) => { if (html) morphCartDrawer(html); })
      .then(() => fetchAndUpdateCartCount())
      .then(() => { clearDawnCartErrors(); })
      .catch(() => { clearDawnCartErrors(); });
  }

  function isCartOk(resp, data) {
    if (!resp || !resp.ok) return false;
    if (data && data.status && data.status !== 200) return false;
    if (data && data.errors) return false;
    return true;
  }

  function postCartChange(body) {
    /* TASK-2962 — locale-prefix the mutation endpoint. Shopify renders the
       returned `sections` HTML (cart-drawer, incl. its empty state) in the
       locale of the REQUEST path; a naked `/cart/change.js` falls back to the
       store default locale (de), leaking German into the drawer on markets
       like ru-de after removing the last line. localeRoot() → e.g. `/ru-de`. */
    return fetch(localeRoot() + '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
    .then((r) => r.json().then((data) => ({ resp: r, data })).catch(() => ({ resp: r, data: null })));
  }

  function cartChangeByKey(lineKey, quantity, opts) {
    opts = opts || {};
    const retried = !!opts.retried;
    const body = { id: lineKey, quantity: quantity, sections: ['cart-drawer', 'cart-icon-bubble'] };
    function recoverAndMaybeRetry() {
      return silentRecover().then(function () {
        if (retried) return null;
        return new Promise(function (resolve) {
          setTimeout(function () {
            cartChangeByKey(lineKey, quantity, { retried: true }).then(resolve, resolve);
          }, 320);
        });
      });
    }
    return postCartChange(body).then(function (out) {
      if (!isCartOk(out.resp, out.data)) return recoverAndMaybeRetry();
      const sectionHtml = out.data && out.data.sections && out.data.sections['cart-drawer'];
      if (sectionHtml) morphCartDrawer(sectionHtml);
      if (out.data && typeof out.data.item_count === 'number') updateCartCount(out.data.item_count);
      if (out.data && typeof out.data.total_price === 'number') updateRewardsFromTotalCents(out.data.total_price);
      clearDawnCartErrors();
      scheduleGiftSync();
      return out.data;
    }, recoverAndMaybeRetry);
  }

  function getLineKeyFromNode(node) {
    if (!node || !node.closest) return null;
    const item = node.closest('.item[data-line-key]');
    if (item && item.dataset.lineKey) return item.dataset.lineKey;
    const keyed = node.closest('[data-quantity-line-key]');
    if (keyed && keyed.dataset.quantityLineKey) return keyed.dataset.quantityLineKey;
    return null;
  }

  function wireKeyBasedQty() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    drawer.addEventListener('click', function (e) {
      const btn = e.target.closest && e.target.closest('button[name="minus"], button[name="plus"]');
      if (!btn) return;
      if (isGiftLine(btn)) return;
      const stepper = btn.closest('quantity-input, .stepper');
      if (!stepper) return;
      const input = stepper.querySelector('input[type="number"], .q');
      if (!input) return;
      const lineKey = getLineKeyFromNode(input) || getLineKeyFromNode(btn);
      if (!lineKey) return;

      const cur = parseInt((input.value || input.textContent || '1'), 10) || 1;
      let next = cur;
      if (btn.name === 'minus') next = Math.max(1, cur - 1);
      else if (btn.name === 'plus') next = cur + 1;
      if (next === cur) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      input.value = String(next);
      cartChangeByKey(lineKey, next);
    }, true);

    drawer.addEventListener('change', function (e) {
      const input = e.target.closest && e.target.closest('input.quantity__input, input.q');
      if (!input) return;
      if (isGiftLine(input)) return;
      const lineKey = getLineKeyFromNode(input);
      if (!lineKey) return;
      let next = parseInt(input.value || '1', 10);
      if (!Number.isFinite(next) || next < 1) next = 1;
      input.value = String(next);

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      cartChangeByKey(lineKey, next);
    }, true);
  }

  function wireVariantSelect() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    drawer.addEventListener('change', function (e) {
      const sel = e.target.closest('[data-ils-variant-sel]');
      if (!sel) return;
      const newVariantId = parseInt(sel.value, 10);
      if (!newVariantId) return;
      const lineKey = getLineKeyFromNode(sel);
      if (!lineKey) return;
      const qtyInput = sel.closest('.item') && sel.closest('.item').querySelector('input.quantity__input, input.q');
      const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value || '1', 10) || 1) : 1;

      sel.disabled = true;
      cartChangeByKey(lineKey, 0).then(function () {
        /* TASK-2962 — locale-prefix so the returned cart-drawer section is
           rendered in the active market locale, not the store default. */
        return fetch(localeRoot() + '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: newVariantId, quantity: qty }], sections: ['cart-drawer', 'cart-icon-bubble'] })
        })
        .then((r) => r.json().then((data) => ({ resp: r, data })).catch(() => ({ resp: r, data: null })))
        .then(function (out) {
          if (!isCartOk(out.resp, out.data)) return silentRecover();
          const sectionHtml = out.data && out.data.sections && out.data.sections['cart-drawer'];
          if (sectionHtml) morphCartDrawer(sectionHtml);
          clearDawnCartErrors();
          scheduleGiftSync();
          fetchAndUpdateCartCount();
        });
      })
      .catch(() => { silentRecover(); })
      .then(() => { sel.disabled = false; }, () => { sel.disabled = false; });
    });
  }

  /* TASK-2131 — synchronous, idempotent guarantee that the upsell
     strip (<section data-ils-cd-upsell>) is present, unhidden, and
     populated after any cart-drawer mutation. Called at the end of
     morphCartDrawer and on drawer open. Rationale: previous fixes
     (TASK-2118 → 2129) preserved the DOM subtree across morph, but
     races still surfaced when either (a) the wrapper was momentarily
     missing at the moment of check, or (b) the list was empty AND
     `lastUpsellListHtml` was empty (very first cart mutation with no
     prior render), and the async `loadUpsell` chain hadn't landed
     yet. This helper closes both holes synchronously by falling back
     to the server-rendered fallback JSON as a last-resort seed. */
  function ensureUpsellPopulated(scope) {
    const root = scope || document.querySelector('cart-drawer');
    if (!root) return;
    let section = root.querySelector('[data-ils-cd-upsell]');
    if (!section) return;
    let list = section.querySelector('[data-ils-cd-upsell-list]');
    if (!list) return;
    section.hidden = false;
    if (list.children.length > 0) {
      /* Keep the visible cards + refresh the string cache. */
      if (list.innerHTML) lastUpsellListHtml = list.innerHTML;
      return;
    }
    if (lastUpsellListHtml) {
      list.innerHTML = lastUpsellListHtml;
      hydrateUpsellRatings(list);
      return;
    }
    /* Last-resort synchronous seed from the Liquid-embedded fallback JSON. */
    try {
      const tag = document.querySelector('script[data-ils-cd-upsell-fallback]');
      if (tag) {
        const arr = JSON.parse(tag.textContent || '[]');
        if (Array.isArray(arr) && arr.length > 0) {
          const html = arr.slice(0, UPSELL_TARGET).map(buildUpsellCard).join('');
          if (html) {
            list.innerHTML = html;
            lastUpsellListHtml = list.innerHTML;
            hydrateUpsellRatings(list);
          }
        }
      }
    } catch (e) { /* swallow — the async loadUpsell chain will still run */ }
  }

  function morphCartDrawer(sectionHtml) {
    if (!sectionHtml) return;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newInner = doc.querySelector('#CartDrawer');
    const liveInner = document.querySelector('#CartDrawer');
    if (newInner && liveInner) {
      /* TASK-2125 — snapshot the live upsell list innerHTML BEFORE any
         detach/swap so `lastUpsellListHtml` always reflects the freshest
         known-good cards. TASK-2121's cache update happened AFTER the
         reattach, so any path that skipped reattach (fresh HTML missing
         the section, race with a concurrent renderPicks, etc.) left the
         cache stale and the recovery fallback broken. */
      const liveUpsell = liveInner.querySelector('[data-ils-cd-upsell]');
      const liveUpsellList = liveUpsell && liveUpsell.querySelector('[data-ils-cd-upsell-list]');
      if (liveUpsellList && liveUpsellList.children.length > 0 && liveUpsellList.innerHTML) {
        lastUpsellListHtml = liveUpsellList.innerHTML;
      }

      /* TASK-2121 physical detach/reattach: cards, listeners, lazy image
         state and hydrated ratings survive the innerHTML swap unchanged. */
      const preserveUpsell = liveUpsellList && liveUpsellList.children.length > 0 ? liveUpsell : null;
      if (preserveUpsell && preserveUpsell.parentNode) {
        preserveUpsell.parentNode.removeChild(preserveUpsell);
      }

      liveInner.innerHTML = newInner.innerHTML;

      const freshUpsell = liveInner.querySelector('[data-ils-cd-upsell]');
      if (freshUpsell) {
        if (preserveUpsell && freshUpsell.parentNode) {
          preserveUpsell.hidden = false;
          freshUpsell.parentNode.replaceChild(preserveUpsell, freshUpsell);
        } else if (lastUpsellListHtml) {
          /* No physical subtree to reattach — restore from the string cache
             so the strip is never a bare title bar. */
          const freshList = freshUpsell.querySelector('[data-ils-cd-upsell-list]');
          if (freshList) freshList.innerHTML = lastUpsellListHtml;
          freshUpsell.hidden = false;
        } else {
          freshUpsell.hidden = false;
        }
      }

      /* TASK-2131 belt-and-suspenders (strengthens TASK-2125). After
         swap+reattach, guarantee the upsell strip is (a) present in
         the DOM, (b) visible (not hidden), and (c) has content in the
         list. If any of those fail, restore from cache; if cache is
         empty (first-ever mutation with no prior render), seed
         synchronously from the server fallback JSON so the strip
         never leaves a bare title bar during the race window between
         morph and the async recommendations chain. */
      ensureUpsellPopulated(liveInner);

      /* TASK-2121 — re-apply the last-known live-leader value so the row
         doesn't regress to the Liquid fallback (€0,00) after the
         innerHTML swap. Without this the row shows the stale value for
         up to 30s (custom-weekly-leader-live.js's poll interval). */
      if (window.ILS && typeof window.ILS.applyLeaderLive === 'function') {
        window.ILS.applyLeaderLive();
      }

      loadUpsell();
    }
    setTimeout(() => {
      initRewards();
      hydrateLeader();
      initUpsellWave();
    }, 0);
  }

  function wireContinueShopping() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    drawer.addEventListener('click', function (e) {
      const link = e.target.closest('[data-ils-cd-continue]');
      if (!link) return;
      e.preventDefault();
      /* TASK-2961 — the drawer is an overlay on the current page, so
         "Continue shopping" just dismisses the cart and keeps the shopper
         exactly where they opened it. No history.back()/navigation (which
         used to wrongly pull them off the page they were browsing). */
      try { if (typeof drawer.close === 'function') drawer.close(); } catch (err) {}
      /* TASK-3352 — but when the drawer was opened on a page with nothing to
         "continue" (the home page, /cart, or the delivery-selector), just closing
         it strands the shopper there (owner saw the drawer's "Continue shopping"
         appear to lead to the home page). In that case send them to the catalog
         (all products, popular-first) via the button's data-fallback-href instead.
         On a real browsable page (product, collection, …) we keep the close-and-
         stay behaviour above. */
      try {
        const here = (window.location && location.pathname) || '';
        const stripped = ilsStripLocale(here);
        const deadEnd = stripped === '/' || stripped === '' ||
                        ilsIsCartPath(here) ||
                        !!document.querySelector('[data-ils-dsel]');
        const fb = link.getAttribute('data-fallback-href');
        if (deadEnd && fb) { setTimeout(function () { window.location.href = fb; }, 20); }
      } catch (err2) {}
    });
  }

  /* TASK-3340 — Checkout gate (drawer). The drawer "Next" button is no longer a
     form-submit to /checkout (see cart-drawer.liquid); it is a plain
     [data-ils-checkout-gate] button that we redirect to /pages/delivery-selector,
     where the shopper picks the delivery method and the delivery_* cart attributes
     our carrier service needs get written. First iteration = ALWAYS route through
     the selector. Delegated on <cart-drawer> so it survives drawer re-renders,
     mirroring wireContinueShopping(). The button is `disabled` (Liquid) on an empty
     cart, so an empty cart cannot reach the selector from here. */
  function wireCheckoutGate() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.addEventListener('click', function (e) {
      const btn = e.target.closest && e.target.closest('[data-ils-checkout-gate]');
      if (!btn) return;
      e.preventDefault();
      if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');
      /* TASK-5777 — see the identical hook in assets/custom-cart-page.js. */
      try {
        const dexp = window.ILSDeliveryExpress;
        if (dexp && typeof dexp.handleGate === 'function' && dexp.handleGate(btn)) return;
      } catch (e5777) {}
      // TASK-3539: go straight to the LOCALIZED selector URL emitted by Liquid
      // (custom-localize-url -> pages['delivery-selector'].url = translated handle,
      // e.g. lv /pages/izvelieties-piegadi). The base handle 301-redirects to it
      // (~1s); direct lands on 200. Fall back to the base handle if attr absent.
      const target = btn.getAttribute('data-ils-selector-url');
      window.location.href = (target && target.charAt(0) === '/')
        ? target
        : (localeRoot() + '/pages/delivery-selector');
    });
    // TASK-3539: bfcache reset. Browser BACK from the selector restores the drawer
    // host page from bfcache with the gate button frozen aria-busy='true' +
    // is-loading, so the guard made the next click inert until a hard refresh.
    // `pageshow` fires on fresh load AND bfcache restore — clear it on every gate
    // button, mirroring the selector's Next reset (TASK-3220).
    window.addEventListener('pageshow', function () {
      const gates = document.querySelectorAll('[data-ils-checkout-gate]');
      for (let i = 0; i < gates.length; i++) {
        gates[i].removeAttribute('aria-busy');
        gates[i].classList.remove('is-loading');
      }
    });
  }

  function applyStepperMinusVisibility(drawer) {
    if (!drawer) return;
    drawer.querySelectorAll('.ils-cd__items .stepper').forEach(function (stepper) {
      var input = stepper.querySelector('input.quantity__input, input.q');
      if (!input) return;
      var min = parseInt(input.getAttribute('min') || input.dataset.min || '1', 10) || 1;
      var v = parseInt(input.value, 10);
      if (!isFinite(v)) v = min;
      stepper.classList.toggle('is-min', v <= min);
    });
  }
  function wireStepperMinusVisibility() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    applyStepperMinusVisibility(drawer);
    drawer.addEventListener('input', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('input.quantity__input, input.q')) applyStepperMinusVisibility(drawer);
    }, true);
    drawer.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('input.quantity__input, input.q')) applyStepperMinusVisibility(drawer);
    }, true);
    drawer.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var btn = e.target.closest('button[name="minus"], button[name="plus"]');
      if (!btn) return;
      setTimeout(function () { applyStepperMinusVisibility(drawer); }, 0);
    });
  }

  function isGiftLine(node) {
    if (!node) return false;
    const item = node.closest && node.closest('.item[data-is-gift], .item[data-line-index]');
    if (!item) return false;
    if (item.getAttribute('data-is-gift') === 'true') return true;
    const skuEl = item.querySelector('.sku');
    return !!(skuEl && /DF021F/i.test(skuEl.textContent || ''));
  }
  function wireMinusLock() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    drawer.addEventListener('click', function (e) {
      const btn = e.target.closest('button[name="minus"], button[name="plus"]');
      if (!btn) return;
      if (isGiftLine(btn)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }
      if (btn.name === 'minus') {
        const stepper = btn.closest('quantity-input, .stepper');
        if (!stepper) return;
        const input = stepper.querySelector('input[type="number"], .q');
        const cur = parseInt((input && (input.value || input.textContent)) || '1', 10);
        if (cur <= 1) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      }
    }, true);

    drawer.addEventListener('keydown', function (e) {
      const input = e.target.closest && e.target.closest('input.quantity__input, input.q');
      if (input && isGiftLine(input)) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    drawer.addEventListener('input', function (e) {
      const input = e.target.closest && e.target.closest('input.quantity__input, input.q');
      if (input && isGiftLine(input)) { input.value = '1'; }
    }, true);
  }

  function wireRemoveConfirm() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const modal = drawer.querySelector('[data-ils-cd-confirm]');
    if (!modal) return;

    const btnCancel = modal.querySelector('[data-ils-cd-confirm-cancel]');
    const btnOk = modal.querySelector('[data-ils-cd-confirm-ok]');
    let pendingLineKey = null;

    function openConfirm(lineKey) {
      pendingLineKey = lineKey;
      modal.hidden = false;
      modal.classList.add('on');
    }
    function closeConfirm() {
      modal.classList.remove('on');
      modal.hidden = true;
      pendingLineKey = null;
    }

    drawer.addEventListener('click', function (e) {
      const rm = e.target.closest('.rm');
      if (!rm) return;
      const item = rm.closest('.item[data-line-key], .item[data-line-index]');
      if (!item) return;
      e.preventDefault();
      e.stopPropagation();
      const lineKey = (item.dataset && item.dataset.lineKey) || getLineKeyFromNode(rm);
      if (!lineKey) return;
      openConfirm(lineKey);
    }, true);

    btnCancel.addEventListener('click', closeConfirm);
    modal.addEventListener('click', function (e) { if (e.target === modal) closeConfirm(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('on')) closeConfirm();
    });

    btnOk.addEventListener('click', function () {
      const key = pendingLineKey;
      closeConfirm();
      if (!key) return;
      cartChangeByKey(key, 0);
    });
  }

  const PLUS_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 4h2v7h7v2h-7v7h-2v-7H4v-2h7z"/></svg>';
  const STAR_PATH = 'M12 2.6c.55 0 1.04.33 1.27.83l2.3 4.96 5.43.7c.55.07.99.45 1.16.97.16.51-.01 1.07-.42 1.43l-4.02 3.52 1.06 5.4c.1.54-.12 1.08-.57 1.4-.45.31-1.04.34-1.52.07L12 18.86l-4.69 2.61c-.48.27-1.07.24-1.52-.07-.45-.32-.67-.86-.57-1.4l1.06-5.4-4.02-3.52c-.41-.36-.58-.92-.42-1.43.17-.52.61-.9 1.16-.97l5.43-.7 2.3-4.96c.23-.5.72-.83 1.27-.83z';
  function starSvg(state) {
    if (state === 'full') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + STAR_PATH + '"/></svg>';
    if (state === 'dim')  return '<svg viewBox="0 0 24 24" class="dim" aria-hidden="true"><path d="' + STAR_PATH + '"/></svg>';
    const gid = 'hg' + Math.random().toString(36).slice(2, 8);
    return '<svg viewBox="0 0 24 24" aria-hidden="true">'
      + '<defs><linearGradient id="' + gid + '"><stop offset="50%" stop-color="var(--star)"/><stop offset="50%" stop-color="var(--star-dim)"/></linearGradient></defs>'
      + '<path d="' + STAR_PATH + '" fill="url(#' + gid + ')"/></svg>';
  }
  function starsHtml(rate) {
    let html = '';
    for (let i = 1; i <= 5; i++) {
      if (rate >= i)         html += starSvg('full');
      else if (rate >= i - .5) html += starSvg('half');
      else                   html += starSvg('dim');
    }
    return html;
  }

  function variantPriceCents(v) { return (v && v.price != null) ? Math.round(parseFloat(v.price)) : 0; }
  function variantCompareCents(v) {
    return (v && v.compare_at_price != null && parseFloat(v.compare_at_price) > parseFloat(v.price))
      ? Math.round(parseFloat(v.compare_at_price)) : 0;
  }

  function buildUpsellCard(product) {
    const url = product.url || '/products/' + (product.handle || '');
    const image = product.featured_image || (product.images && product.images[0]) || '';
    const imgSrc = image ? (typeof image === 'string' ? image : image.src || image) : '';
    const title = product.title || '';
    const variants = Array.isArray(product.variants) ? product.variants.filter(v => v && v.available !== false) : [];
    const allVariants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants[0] || allVariants[0] || null;
    if (!variant) return '';

    const opts = Array.isArray(product.options) ? product.options : [];
    const hasMultipleVariants = (opts.length === 1) && (allVariants.length > 1);
    const hasSingleVariant = (opts.length === 1) && (allVariants.length === 1);
    const hasWeightSel = hasMultipleVariants;

    const priceCents = variantPriceCents(variant);
    const compareCents = variantCompareCents(variant);
    const isSale = compareCents > 0;

    const priceHtml = isSale
      ? '<div class="price-col" data-up-price>'
        + '<span class="pr-old" data-up-old>' + moneyFromCents(compareCents) + '</span>'
        + '<span class="pr" data-up-now>' + moneyFromCents(priceCents) + '</span>'
        + '<span class="save" data-up-save>' + upsellSaveLabel(compareCents - priceCents) + '</span>'
        + '</div>'
      : '<div class="price-col" data-up-price>'
        + '<span class="pr" data-up-now>' + moneyFromCents(priceCents) + '</span>'
        + '</div>';

    const imgHtml = imgSrc
      ? '<img class="ph" src="' + imgSrc + '&width=216" alt="' + escapeHtml(title) + '" loading="lazy" width="108" height="108">'
      : '<span class="ph ph--placeholder" aria-hidden="true">🛒</span>';

    const variantsPayload = allVariants.map(function (v) {
      return {
        id: v.id,
        title: v.title || (v.options && v.options[0]) || '',
        sku: v.sku || '',
        available: v.available !== false,
        price_cents: variantPriceCents(v),
        compare_cents: variantCompareCents(v)
      };
    });

    let selHtml = '';
    if (hasMultipleVariants) {
      const curLabel = formatWeight(variant.title || (variant.options && variant.options[0]) || '');
      selHtml = ''
        + '<button type="button" class="sel sel--btn" data-up-sel-btn aria-label="' + escapeHtml(rwI18n().selectWeight) + '" aria-haspopup="dialog">'
          + '<span class="sel__lbl" data-up-sel-lbl>' + escapeHtml(curLabel) + '</span>'
          + '<svg class="sel__chev" viewBox="0 0 10 10" aria-hidden="true"><path fill="#7A6F61" d="M5 7L1 3h8z"/></svg>'
        + '</button>';
    } else if (hasSingleVariant) {
      const curLabel = formatWeight(variant.title || (variant.options && variant.options[0]) || '');
      selHtml = '<div class="sel sel--static">'
        + '<span class="sel__lbl">' + escapeHtml(curLabel) + '</span>'
        + '</div>';
    } else {
      selHtml = '<div></div>';
    }

    const handle = product.handle || '';
    const ratingHtml = '<div class="rate" data-up-rate data-up-handle="' + escapeHtml(handle) + '" hidden></div>';

    const variantsAttr = hasWeightSel ? escapeHtml(JSON.stringify(variantsPayload)) : '';
    return ''
      + '<div class="item' + (isSale ? ' is-sale' : '') + (hasWeightSel ? ' has-sel' : '')
        + '" data-upsell-variant-id="' + variant.id + '"'
        + (hasWeightSel ? ' data-variants="' + variantsAttr + '"' : '') + '>'
        + '<a class="ph-link" href="' + url + '" tabindex="-1" aria-hidden="true">' + imgHtml + '</a>'
        + '<div class="mid">'
          + '<a class="nm" href="' + url + '">' + escapeHtml(title) + '</a>'
          + ratingHtml
          + '<div class="bottom">'
            + selHtml
            + priceHtml
          + '</div>'
        + '</div>'
        + '<button class="add" data-upsell-add type="button" aria-label="Add to cart">' + PLUS_SVG + '</button>'
      + '</div>';
  }

  function hydrateUpsellRatings(listEl) {
    if (!listEl) return;
    /* TASK-2563 — localized review word (singular/plural) emitted by Liquid
       via `| t` on the upsell host; English fallback keeps parity if absent. */
    const upHost = listEl.closest('[data-ils-cd-upsell]') || document.querySelector('[data-ils-cd-upsell]');
    const revWordOne = (upHost && upHost.getAttribute('data-review-word-one')) || 'review';
    const revWordOther = (upHost && upHost.getAttribute('data-review-word-other')) || 'reviews';
    const cards = listEl.querySelectorAll('[data-up-rate]');
    cards.forEach(function (rateEl) {
      const handle = rateEl.getAttribute('data-up-handle');
      if (!handle) return;
      const card = rateEl.closest('[data-upsell-variant-id]');
      const url = '/products/' + encodeURIComponent(handle) + '?section_id=ils-upsell-rating';
      fetch(url, { headers: { Accept: 'text/html' } })
        .then((r) => r.ok ? r.text() : '')
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const node = doc.querySelector('[data-ils-rating]');
          if (!node) return;
          /* TASK-2424 — persist preorder flag+date on the card so the
             on-click guard in wireUpsellAdd / addToCartFromModal can
             consult it synchronously without another network round-trip. */
          if (card) {
            const poActive = node.getAttribute('data-ils-preorder-active') || '0';
            const poDate = node.getAttribute('data-ils-preorder-date') || '';
            card.setAttribute('data-ils-preorder-active', poActive);
            card.setAttribute('data-ils-preorder-date', poDate);
            /* TASK-5009 — carry the product row payload too. */
            ['product', 'image', 'url', 'variants'].forEach(function (k) {
              const poExtra = node.getAttribute('data-ils-preorder-' + k);
              if (poExtra !== null) card.setAttribute('data-ils-preorder-' + k, poExtra);
            });
            card.setAttribute('data-ils-preorder-decorated', '1');
          }
          const rating = parseFloat(node.getAttribute('data-ils-rating') || '0') || 0;
          const count = parseInt(node.getAttribute('data-ils-rating-count') || '0', 10) || 0;
          if (count <= 0) return;
          rateEl.innerHTML =
            '<span class="rate-stars" aria-hidden="true">' + starsHtml(rating) + '</span>'
            + '<span class="cnt">(' + count + ' ' + (count === 1 ? revWordOne : revWordOther) + ')</span>';
          rateEl.hidden = false;
        })
        .catch(() => {});
    });
  }

  /* TASK-2424 — Preorder guard for direct fetch('/cart/add.js') calls.
     Returns a Promise<boolean>: true = proceed with add, false = user
     cancelled. If the card was already decorated by hydrateUpsellRatings
     the decision is synchronous; otherwise we fetch the same section
     once (and cache the result on the card) so the popup never gets
     bypassed on a fast click. */
  function checkAndConfirmPreorder(card) {
    if (!card) return Promise.resolve(true);

    function decide() {
      const a = card.getAttribute('data-ils-preorder-active');
      const d = card.getAttribute('data-ils-preorder-date') || '';
      if (a === '1' && d) {
        if (window.ILSPreorderPopup && typeof window.ILSPreorderPopup.confirm === 'function') {
          /* TASK-5009 — the popup now shows the real product row. */
          return window.ILSPreorderPopup.confirm({
            date: d,
            variantId: card.getAttribute('data-upsell-variant-id') || '',
            quantity: 1,
            title: card.getAttribute('data-ils-preorder-product') || '',
            image: card.getAttribute('data-ils-preorder-image') || '',
            url: card.getAttribute('data-ils-preorder-url') || '',
            variants: card.getAttribute('data-ils-preorder-variants') || ''
          });
        }
      }
      return Promise.resolve(true);
    }

    if (card.getAttribute('data-ils-preorder-decorated') === '1') {
      return decide();
    }

    const handleEl = card.querySelector('[data-up-handle]');
    const handle = handleEl ? handleEl.getAttribute('data-up-handle') : '';
    if (!handle) return decide();

    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=ils-upsell-rating', {
      headers: { Accept: 'text/html' }
    })
      .then((r) => r.ok ? r.text() : '')
      .then((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('[data-ils-rating]');
        if (!node) return;
        const poActive = node.getAttribute('data-ils-preorder-active') || '0';
        const poDate = node.getAttribute('data-ils-preorder-date') || '';
        card.setAttribute('data-ils-preorder-active', poActive);
        card.setAttribute('data-ils-preorder-date', poDate);
        /* TASK-5009 — carry the product row payload too. */
        ['product', 'image', 'url', 'variants'].forEach(function (k) {
          const poExtra = node.getAttribute('data-ils-preorder-' + k);
          if (poExtra !== null) card.setAttribute('data-ils-preorder-' + k, poExtra);
        });
        card.setAttribute('data-ils-preorder-decorated', '1');
      })
      .catch(() => {})
      .then(decide);
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  }

  const UPSELL_TARGET = 9;
  let upsellRunId = 0;
  /* TASK-2118 — module-level snapshot of the last non-empty rendered
     upsell list. morphCartDrawer() reads this as a fallback when the
     DOM's current list is empty at morph time (rapid double-morph
     race after removing a line with qty > 1). Updated by renderPicks
     and by the sync seed painter below. */
  let lastUpsellListHtml = '';
  function loadUpsell() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const section = drawer.querySelector('[data-ils-cd-upsell]');
    const list = drawer.querySelector('[data-ils-cd-upsell-list]');
    if (!section || !list) return;

    const cartItemEls = Array.from(drawer.querySelectorAll('.ils-cd__items .item[data-product-id]'));
    if (cartItemEls.length === 0) {
      /* TASK-2129 — cart just went empty (single item removed). Instead of
         hiding the strip + wiping the list, keep whatever cards were
         showing (or restore from the server fallback) so «WE RECOMMEND»
         doesn't disappear when the user removes their last item. */
      if (list.children && list.children.length > 0) {
        section.hidden = false;
        if (list.innerHTML) lastUpsellListHtml = list.innerHTML;
        return;
      }
      if (lastUpsellListHtml) {
        list.innerHTML = lastUpsellListHtml;
        section.hidden = false;
        hydrateUpsellRatings(list);
        return;
      }
      /* Nothing cached yet — seed from the server-rendered fallback JSON. */
      try {
        const seed = readServerFallback();
        if (seed && seed.length > 0) {
          renderPicks(seed.slice(0, UPSELL_TARGET));
          return;
        }
      } catch (e) {}
      section.hidden = true;
      list.innerHTML = '';
      return;
    }

    const myRun = ++upsellRunId;
    const cartProductIds = new Set(cartItemEls.map((el) => String(el.dataset.productId)));
    const productIdsInOrder = cartItemEls.map((el) => String(el.dataset.productId));

    function mergeFresh(acc, seenIds, products) {
      if (!Array.isArray(products)) return acc;
      for (let i = 0; i < products.length; i++) {
        if (acc.length >= UPSELL_TARGET) break;
        const p = products[i];
        if (!p) continue;
        const id = String(p.id);
        if (cartProductIds.has(id)) continue;
        if (seenIds.has(id)) continue;
        seenIds.add(id);
        acc.push(p);
      }
      return acc;
    }

    function fetchRecs(productId, intent) {
      const url = localeRoot() + '/recommendations/products.json?product_id='
        + encodeURIComponent(productId) + '&limit=20&intent=' + intent;
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => (data && Array.isArray(data.products)) ? data.products : [])
        .catch(() => []);
    }

    function fetchGeneric() {
      return fetch(localeRoot() + '/products.json?limit=50', { headers: { Accept: 'application/json' } })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => (data && Array.isArray(data.products)) ? data.products : [])
        .catch(() => []);
    }

    function readServerFallback() {
      const tag = document.querySelector('script[data-ils-cd-upsell-fallback]');
      if (!tag) return [];
      try {
        const arr = JSON.parse(tag.textContent || '[]');
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        console.warn('[ILS upsell] server fallback JSON parse failed:', e);
        return [];
      }
    }

    function renderPicks(picks) {
      list.innerHTML = picks.map(buildUpsellCard).join('');
      section.hidden = false;
      /* TASK-2118 — snapshot after every render so a subsequent morph
         can restore even if it captures an empty DOM list (race). */
      if (list.innerHTML) lastUpsellListHtml = list.innerHTML;
      hydrateUpsellRatings(list);
    }

    if (!list.children || list.children.length === 0) {
      const seedSeen = new Set();
      const seed = [];
      mergeFresh(seed, seedSeen, readServerFallback());
      if (seed.length > 0) {
        renderPicks(seed.slice(0, UPSELL_TARGET));
      } else if (lastUpsellListHtml) {
        /* TASK-2118 — server fallback returned nothing usable AND the
           DOM list was empty. Restore from the cache so the strip is
           not a bare title bar while the async chain runs. */
        list.innerHTML = lastUpsellListHtml;
        section.hidden = false;
        hydrateUpsellRatings(list);
      }
    } else {
      section.hidden = false;
      /* TASK-2118 — warm the cache from the currently-visible list so a
         subsequent morph can restore even if it captures an empty DOM. */
      if (list.innerHTML) lastUpsellListHtml = list.innerHTML;
    }

    (async function chain() {
      const picks = [];
      const seenIds = new Set();

      {
        const got = await fetchRecs(productIdsInOrder[0], 'related');
        mergeFresh(picks, seenIds, got);
      }
      if (picks.length < UPSELL_TARGET) {
        const got = await fetchRecs(productIdsInOrder[0], 'complementary');
        mergeFresh(picks, seenIds, got);
      }
      for (let i = 1; i < productIdsInOrder.length && picks.length < UPSELL_TARGET; i++) {
        const gotR = await fetchRecs(productIdsInOrder[i], 'related');
        mergeFresh(picks, seenIds, gotR);
        if (picks.length < UPSELL_TARGET) {
          const gotC = await fetchRecs(productIdsInOrder[i], 'complementary');
          mergeFresh(picks, seenIds, gotC);
        }
      }
      if (picks.length < UPSELL_TARGET) {
        const got = await fetchGeneric();
        mergeFresh(picks, seenIds, got);
      }
      if (picks.length < UPSELL_TARGET) {
        const got = readServerFallback();
        mergeFresh(picks, seenIds, got);
      }

      if (myRun !== upsellRunId) return;
      if (picks.length > 0) {
        renderPicks(picks.slice(0, UPSELL_TARGET));
      } else if (list.children && list.children.length > 0) {
        section.hidden = false;
        if (list.innerHTML) lastUpsellListHtml = list.innerHTML;
      } else if (lastUpsellListHtml) {
        /* TASK-2118 — last resort: chain returned nothing and the list
           is empty. Paint the cache so we never leave a bare title bar
           after a cart mutation. */
        list.innerHTML = lastUpsellListHtml;
        section.hidden = false;
        hydrateUpsellRatings(list);
      }
    })();
  }

  function wireUpsellAdd() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    drawer.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-upsell-add]');
      if (!btn) return;
      e.preventDefault();
      const card = btn.closest('[data-upsell-variant-id]');
      if (!card) return;
      const variantId = card.dataset.upsellVariantId;
      if (!variantId) return;

      btn.disabled = true;
      /* TASK-2424 — guard the direct /cart/add.js call behind the same
         preorder popup used by <product-form> submits (TASK-2066). */
      checkAndConfirmPreorder(card).then((ok) => {
        if (!ok) { btn.disabled = false; return; }
        const body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }], sections: ['cart-drawer', 'cart-icon-bubble'] };
        /* TASK-2962 — locale-prefix the add endpoint (section leak class). */
        fetch(localeRoot() + '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.status && data.status !== 200) return;
          return fetch(window.routes && routes.cart_url ? routes.cart_url + '?section_id=cart-drawer' : localeRoot() + '/cart?section_id=cart-drawer' /* TASK-2962 locale-aware fallback */)
            .then((r) => r.text())
            .then((html) => {
              morphCartDrawer(html);
              const wrap = document.querySelector('cart-drawer');
              if (wrap) wrap.classList.remove('is-empty');
              fetchAndUpdateCartCount();
              notifyGiftReconciler(); /* TASK-2674 — adding an upsell from the
                 drawer can cross a gift threshold; wake the reconciler so the
                 gift is added/upgraded (same silent-drawer-path class as remove). */
            });
        })
        .catch(() => {})
        .finally(() => { btn.disabled = false; });
      });
    });
  }

  function wireMorphObserver() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    let queued = false;
    const flush = () => {
      queued = false;
      initRewards();
      applyStepperMinusVisibility(drawer);
      initCdPayMarquee();
      /* TASK-2136 — belt-and-suspenders for BUG 1 (upsell "WE RECOMMEND"
         losing cards after cart mutation). morphCartDrawer + drawer-open
         side-effects both call ensureUpsellPopulated, but Dawn's own
         CartItems.onCartUpdate (cart.js) subscribes to the same
         PUB_SUB_EVENTS.cartUpdate and does a `targetElement.replaceWith`
         on <cart-drawer-items> and .cart-drawer__footer — races with our
         subtree preserve/reattach dance can theoretically catch the
         upsell subtree in a detached state. Running ensureUpsellPopulated
         from this generic subtree-mutation observer guarantees the strip
         is re-hydrated after ANY drawer DOM change, without needing to
         reason about every possible cart-mutation code path. Cheap:
         ensureUpsellPopulated returns early when the list already has
         children. */
      ensureUpsellPopulated(drawer);
    };
    const mo = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(flush);
    });
    mo.observe(drawer, { childList: true, subtree: true });
  }

  function initCdPayMarquee() {
    const wrap = document.querySelector('[data-ils-cd-pay-marquee]');
    const track = document.querySelector('[data-ils-cd-pay-track]');
    if (!wrap || !track) return;

    /* TASK-2876 — do NOT tear the marquee down on every call (see the idempotency
       guard below). Measure against the server-rendered ORIGINAL sets only; the
       clones never change the set width or the seam delta (both are transform-
       invariant), so they can be measured around instead of removed-and-rebuilt. */
    const clones = track.querySelectorAll('.ils-cd__pay-set[data-ils-cd-pay-clone]');
    const originals = track.querySelectorAll('.ils-cd__pay-set:not([data-ils-cd-pay-clone])');
    const firstSet = originals[0];
    const secondSet = originals[1];
    if (!firstSet) return;

    const setW = firstSet.getBoundingClientRect().width;
    const wrapW = wrap.clientWidth;
    if (!(setW > 0) || !(wrapW > 0)) return;

    /* TASK-2670 — the marquee's per-chip margins are no longer uniform: each
       chip's margins are tuned to cancel its logo's internal transparent
       padding so every VISIBLE gap is a constant 10px (see custom-cart-drawer
       .css). The old `setW + marginLeft` shift assumed a single uniform gap,
       which is now wrong. Measure the exact repeat period directly instead:
       the x-distance between the first chip of the original set and the first
       chip of the duplicated set. That delta IS one full cycle (set width +
       one inter-set gap) whatever the margin split, keeping the loop seamless. */
    const seamC1 = firstSet.querySelector('.sa-pay-chip');
    const seamC2 = secondSet && secondSet.querySelector('.sa-pay-chip');
    let shift;
    if (seamC1 && seamC2) {
      shift = seamC2.getBoundingClientRect().left - seamC1.getBoundingClientRect().left;
    } else {
      shift = setW + 10; // single-set fallback: use the target 10px gap
    }
    if (!(shift > 0)) return;

    const needed = Math.max(originals.length, Math.ceil(wrapW / setW) + 2);

    /* TASK-2876 — IDEMPOTENCY GUARD. initCdPayMarquee() also runs from
       wireMorphObserver's MutationObserver, which fires on ANY drawer subtree
       change. The clone rebuild just below is itself a subtree change, so an
       unconditional rebuild re-triggered this function every frame (measured 228
       track child-mutations/sec) — an infinite teardown/rebuild loop that
       restarted the CSS animation on each pass, so its currentTime never advanced
       and the belt sat frozen (the real "stopped moving entirely" cause). When the
       geometry is unchanged and we're already wired, bail without touching the DOM
       or the animation: the running belt is left alone and the loop settles.
       Rebuild only on a real change — first run, resize, or a market re-render that
       swaps in a fresh track element (fresh element => no __ilsPayMqSig => rebuild). */
    const sig = needed + '|' + shift.toFixed(2) + '|' + Math.round(wrapW);
    if (wrap.classList.contains('is-mq')
        && track.__ilsPayMqSig === sig
        && clones.length === (needed - originals.length)
        && track.style.animationName === 'ilsCdPayMarqueeRun') {
      return;
    }
    track.__ilsPayMqSig = sig;

    clones.forEach(function (n) { n.remove(); });
    for (let i = originals.length; i < needed; i++) {
      const clone = firstSet.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-ils-cd-pay-clone', '');
      track.appendChild(clone);
    }

    const ILS_PAY_MQ_SPEED_PX_PER_SEC = 25;
    const duration = shift / ILS_PAY_MQ_SPEED_PX_PER_SEC;

    /* TASK-2876 — the base rule in custom-cart-drawer.css animates
       `to { transform: translateX(var(--ils-cd-pay-shift)) }`. That endpoint is
       an UNREGISTERED CSS custom property, and Chrome/Safari do NOT interpolate a
       transform whose keyframe value comes from an unregistered var — the belt
       stayed pinned to the `from` frame (translateX(0)) forever. It only *looked*
       fine before because the old `!important` `-50%` override in
       custom-cart-timer.liquid animated a concrete percentage; once TASK-2875
       removed that override the marquee stopped moving entirely (owner: "перестала
       двигаться вообще") even though animation-name/-play-state/is-mq all read
       correct. Fix: emit a keyframe whose endpoint is the CONCRETE computed px and
       point the track at it by name. Concrete lengths interpolate in every browser.
       Redefining the SAME name in an injected <style> is ignored (the first,
       var-based definition wins), so we use a distinct name (ilsCdPayMarqueeRun)
       and override only animation-name inline — duration / linear / infinite still
       come from the .is-mq rule. Seamless: `shift` is the exact one-set repeat
       period (TASK-2670). Speed: `duration = shift / 20` keeps 20 px/s per market. */
    let kfStyle = document.getElementById('ils-cd-pay-mq-kf');
    if (!kfStyle) {
      kfStyle = document.createElement('style');
      kfStyle.id = 'ils-cd-pay-mq-kf';
      document.head.appendChild(kfStyle);
    }
    kfStyle.textContent =
      '@keyframes ilsCdPayMarqueeRun{from{transform:translateX(0)}' +
      'to{transform:translateX(-' + shift.toFixed(2) + 'px)}}';

    track.style.setProperty('--ils-cd-pay-shift', '-' + shift.toFixed(2) + 'px');
    wrap.style.setProperty('--ils-cd-pay-mq-duration', duration.toFixed(2) + 's');
    wrap.classList.add('is-mq');
    track.style.animationName = 'ilsCdPayMarqueeRun';
  }

  function wireCdPayMarqueeResize() {
    if (window.__ilsCdPayResizeBound) return;
    window.__ilsCdPayResizeBound = true;
    let rafToken = null;
    window.addEventListener('resize', function () {
      if (rafToken) cancelAnimationFrame(rafToken);
      rafToken = requestAnimationFrame(initCdPayMarquee);
    });
  }

  function hydrateLeader() {
    const row = document.querySelector('[data-ils-cd-leader-row]');
    const amt = document.querySelector('[data-ils-cd-leader]');
    if (!row || !amt) return;
    let value = (row.getAttribute('data-ils-cd-leader-original') || '').trim();
    if (!value) value = (amt.textContent || '').trim();
    const onPage = document.getElementById('weekly_leader_amount');
    if (onPage) {
      const live = (onPage.textContent || '').trim();
      if (live) {
        value = live;
        row.setAttribute('data-ils-cd-leader-original', live);
      }
    }
    /* TASK-2121 — on pages without #weekly_leader_amount (product,
       collection, cart), morphCartDrawer resets `data-ils-cd-leader-original`
       to the Liquid fallback. Prefer the module-level live value cached
       by custom-weekly-leader-live.js so the row doesn't briefly regress
       to €0,00 after each cart mutation. */
    const liveCached = window.ILS && window.ILS._leaderLiveValue;
    if (liveCached) {
      value = liveCached;
      row.setAttribute('data-ils-cd-leader-original', liveCached);
    }
    if (!value) {
      row.hidden = true;
      return;
    }
    if (!amt.dataset.ilsLeaderHydrated) {
      amt.textContent = value;
      amt.dataset.ilsLeaderHydrated = '1';
    }
    row.hidden = false;
  }

  function initUpsellWave() {
    const banner = document.querySelector('[data-ils-cd-upsell-banner]');
    if (!banner || banner.dataset.waveBound) return;
    banner.dataset.waveBound = '1';
    const interval = parseInt(banner.dataset.waveInterval, 10) || 2500;
    function fire() {
      banner.classList.remove('is-waving');
      void banner.offsetWidth;
      banner.classList.add('is-waving');
    }
    let timer = null;
    function start() { if (!timer) { fire(); timer = setInterval(fire, interval); } }
    function stop() { if (timer) { clearInterval(timer); timer = null; banner.classList.remove('is-waving'); } }
    const drawer = banner.closest('cart-drawer');
    if (drawer) {
      const mo = new MutationObserver(() => {
        if (drawer.classList.contains('active')) start(); else stop();
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      if (drawer.classList.contains('active')) start();
    } else {
      start();
    }
  }

  let weightModal = null;
  function pickAddToCartLabel() {
    var dict = {
      en: 'Add to cart',
      ru: 'Добавить',
      de: 'In den Warenkorb',
      lv: 'Pievienot grozam',
      et: 'Lisa korvi',
      lt: 'Į krepšelį'
    };
    var raw = '';
    try { if (typeof window.ILS_LANG === 'string' && window.ILS_LANG) raw = window.ILS_LANG; } catch (e) {}
    if (!raw) { try { raw = (window.Shopify && Shopify.locale) || ''; } catch (e) {} }
    if (!raw) raw = (document.documentElement && document.documentElement.lang) || '';
    raw = String(raw || '').toLowerCase().slice(0, 2);
    return dict[raw] || dict.en;
  }
  function ensureWeightModal() {
    if (weightModal) return weightModal;
    weightModal = document.createElement('div');
    weightModal.className = 'ils-cd__wmodal-back';
    weightModal.hidden = true;
    weightModal.setAttribute('role', 'dialog');
    weightModal.setAttribute('aria-modal', 'true');
    weightModal.setAttribute('aria-label', rwI18n().selectWeight);
    weightModal.innerHTML =
      '<div class="ils-cd__wmodal">' +
        '<div class="ils-cd__wmodal-head">' +
          '<h3 class="ils-cd__wmodal-title">' + escapeHtml(rwI18n().selectWeight) + '</h3>' +
          '<button type="button" class="ils-cd__wmodal-x" aria-label="Close">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
              '<line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/>' +
            '</svg>' +
          '</button>' +
        '</div>' +
        '<div class="ils-cd__wmodal-tiles" data-ils-cd-wmodal-list></div>' +
        '<button type="button" class="ils-cd__wmodal-add" data-ils-cd-wmodal-add></button>' +
      '</div>';
    document.body.appendChild(weightModal);

    function close() {
      weightModal.classList.remove('on');
      weightModal.hidden = true;
      weightModal._card = null;
      weightModal._selectedVariantId = null;
    }
    weightModal.addEventListener('click', function (e) {
      if (e.target === weightModal) { close(); return; }
      if (e.target.closest('.ils-cd__wmodal-x')) { close(); return; }

      var opt = e.target.closest('[data-wval]');
      if (opt && weightModal._card) {
        if (opt.classList.contains('is-oos') || opt.disabled) return;
        var variantId = opt.getAttribute('data-wval');
        weightModal._selectedVariantId = variantId;
        var tiles = weightModal.querySelectorAll('.ils-cd__wmodal-tile');
        for (var i = 0; i < tiles.length; i++) {
          var on = tiles[i].getAttribute('data-wval') === String(variantId);
          tiles[i].classList.toggle('is-selected', on);
        }
        return;
      }

      var addBtn = e.target.closest('[data-ils-cd-wmodal-add]');
      if (addBtn && weightModal._card) { addToCartFromModal(); return; }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && weightModal.classList.contains('on')) close();
    });
    weightModal._close = close;
    return weightModal;
  }
  function pickWeightRows(n) {
    if (window.ILS && typeof window.ILS.weightRows === 'function') return window.ILS.weightRows(n);
    n = Math.max(0, parseInt(n, 10) || 0);
    if (n === 0) return [];
    if (n <= 4) return [n];
    switch (n) {
      case 5: return [3, 2];
      case 6: return [3, 3];
      case 7: return [4, 3];
      case 8: return [4, 4];
      case 9: return [3, 3, 3];
      case 10: return [4, 3, 3];
      default: {
        var rows = [], rem = n;
        while (rem > 4) { rows.push(4); rem -= 4; }
        if (rem > 0) rows.push(rem);
        return rows;
      }
    }
  }

  function openWeightModal(card) {
    const modal = ensureWeightModal();
    const list = modal.querySelector('[data-ils-cd-wmodal-list]');
    let variants = [];
    try { variants = JSON.parse(card.getAttribute('data-variants') || '[]'); } catch (e) { variants = []; }
    const currentId = String(card.dataset.upsellVariantId || '');

    const rows = pickWeightRows(variants.length);
    let cursor = 0;
    const html = rows.map(function (cols) {
      let inner = '';
      for (let j = 0; j < cols; j++) {
        const v = variants[cursor++];
        if (!v) break;
        const isSel = String(v.id) === currentId;
        const avail = v.available !== false;
        const label = formatWeight(v.title || (v.options && v.options[0]) || '');
        const price = moneyFromCents(parseInt(v.price_cents, 10) || 0);
        inner += ''
          + '<button type="button" class="ils-cd__wmodal-tile' + (isSel ? ' is-selected' : '') + (avail ? '' : ' is-oos') + '"'
            + ' data-wval="' + v.id + '"' + (avail ? '' : ' disabled') + '>'
            + '<span class="ils-cd__wmodal-tile-weight">' + escapeHtml(label) + '</span>'
            + '<span class="ils-cd__wmodal-tile-price">' + price + '</span>'
          + '</button>';
      }
      return '<div class="ils-cd__wmodal-row c' + cols + '">' + inner + '</div>';
    }).join('');
    list.innerHTML = html;
    modal._selectedVariantId = currentId;
    var addBtn = modal.querySelector('[data-ils-cd-wmodal-add]');
    if (addBtn) { addBtn.textContent = pickAddToCartLabel(); addBtn.disabled = false; }
    modal._card = card;
    var titleEl = modal.querySelector('.ils-cd__wmodal-title');
    if (titleEl) {
      var nm = card.querySelector('.nm');
      var name = nm ? (nm.textContent || '').trim() : '';
      titleEl.textContent = name || rwI18n().selectWeight;
    }
    modal.hidden = false;
    modal.classList.add('on');
  }
  function addToCartFromModal() {
    if (!weightModal || !weightModal._card) return;
    var card = weightModal._card;
    var variantId = weightModal._selectedVariantId || card.dataset.upsellVariantId;
    if (!variantId) return;

    applyWeightPick(card, variantId);

    var addBtn = weightModal.querySelector('[data-ils-cd-wmodal-add]');
    if (addBtn) addBtn.disabled = true;

    /* TASK-2424 — guard the direct /cart/add.js call behind the preorder
       popup (same as the WE RECOMMEND «+» button path above). */
    checkAndConfirmPreorder(card).then(function (ok) {
      if (!ok) {
        if (addBtn) addBtn.disabled = false;
        if (weightModal && weightModal._close) weightModal._close();
        return;
      }
      var body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }], sections: ['cart-drawer', 'cart-icon-bubble'] };
      /* TASK-2962 — locale-prefix the add endpoint (section leak class). */
      fetch(localeRoot() + '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.status && data.status !== 200) {
          if (addBtn) addBtn.disabled = false;
          return;
        }
        return fetch(window.routes && routes.cart_url ? routes.cart_url + '?section_id=cart-drawer' : localeRoot() + '/cart?section_id=cart-drawer' /* TASK-2962 locale-aware fallback */)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            morphCartDrawer(html);
            var wrap = document.querySelector('cart-drawer');
            if (wrap) wrap.classList.remove('is-empty');
            fetchAndUpdateCartCount();
          });
      })
      .catch(function () {})
      .finally(function () {
        if (addBtn) addBtn.disabled = false;
        if (weightModal && weightModal._close) weightModal._close();
      });
    });
  }
  function applyWeightPick(card, variantId) {
    let variants = [];
    try { variants = JSON.parse(card.getAttribute('data-variants') || '[]'); } catch (e) { variants = []; }
    const v = variants.find(function (x) { return String(x.id) === String(variantId); });
    if (!v) return;
    const priceCents = parseInt(v.price_cents, 10) || 0;
    const compareCents = parseInt(v.compare_cents, 10) || 0;
    const isSale = compareCents > 0;
    card.dataset.upsellVariantId = v.id;

    const priceColEl = card.querySelector('[data-up-price]');
    if (priceColEl) {
      priceColEl.innerHTML = isSale
        ? '<span class="pr-old" data-up-old>' + moneyFromCents(compareCents) + '</span>'
          + '<span class="pr" data-up-now>' + moneyFromCents(priceCents) + '</span>'
          + '<span class="save" data-up-save>' + upsellSaveLabel(compareCents - priceCents) + '</span>'
        : '<span class="pr" data-up-now>' + moneyFromCents(priceCents) + '</span>';
    }
    card.classList.toggle('is-sale', isSale);

    const btnLbl = card.querySelector('[data-up-sel-lbl]');
    if (btnLbl) btnLbl.textContent = formatWeight(v.title || (v.options && v.options[0]) || '');
  }
  function wireWeightPopup() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-up-sel-btn]');
      if (!btn) return;
      e.preventDefault();
      const card = btn.closest('[data-upsell-variant-id]');
      if (!card) return;
      btn.blur();
      openWeightModal(card);
    });
  }

  function wireOpenCloseSideEffects() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const mo = new MutationObserver(() => {
      const isActive = drawer.classList.contains('active');
      document.body.classList.toggle('ils-cd-open', isActive);
      if (isActive) {
        fetchAndUpdateCartCount();
        initRewards();
        /* TASK-2131 — synchronous guarantee first, async chain second.
           `loadUpsell` populates via /recommendations (async) and only
           fills empty-cart lists from cache OR fallback JSON. That's
           the correct ownership, but async means there's a visible
           blank window if the cache lost cards (e.g. reopen after a
           remove crossed some invisible state). ensureUpsellPopulated
           closes that window synchronously before the async chain
           runs. */
        ensureUpsellPopulated(drawer);
        loadUpsell();
        hydrateLeader();
        initUpsellWave();
        requestAnimationFrame(initCdPayMarquee);
      }
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function parseLeaderEur(raw) {
    if (!raw) return 0;
    const stripped = String(raw).replace(/[^\d.,-]/g, '');
    if (!stripped) return 0;
    let normalized;
    if (stripped.indexOf(',') !== -1 && stripped.indexOf('.') !== -1) {
      normalized = stripped.replace(/\./g, '').replace(',', '.');
    } else if (stripped.indexOf(',') !== -1) {
      normalized = stripped.replace(',', '.');
    } else if (stripped.indexOf('.') !== -1) {
      /* TASK-5946 — '.' alone is a THOUSANDS separator in all 7 presentment
         formats this shop serves (REPORT-5944 B.2: the decimal is always ',').
         Before the zero-fraction trim this branch only saw HUF ("5.000 Ft",
         already misread as 5); after it, it also sees "€1.301". */
      normalized = stripped.replace(/\./g, '');
    } else {
      normalized = stripped;
    }
    const n = parseFloat(normalized);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }
  function getLeaderEur() {
    /* TASK-2129 — prefer the module-level live leader cache first. The DOM
       attribute `data-ils-cd-leader-original` is reset to the Liquid
       fallback (€0,00) on every morphCartDrawer innerHTML swap; even
       though applyLeaderLive restores it synchronously, an early read
       from a mutation-observer flush or a very fast follow-up render
       could still catch the €0,00 window. Reading _leaderLiveValue
       first removes that flash entirely (and keeps the same value
       both custom-weekly-leader-live and this module see). */
    /* TASK-5856 — REPORT-5848 D5's trap, defused. custom-weekly-leader-live.js
       now localises the amount it paints, so the rendered string on /en-se/ is
       "1 100,05 kr", and parsing it back as euros would have turned the leader
       comparison into presentment-vs-EUR — the TASK-5795 defect class, in the
       surface that awards the bonus. The painter therefore publishes the raw EUR
       NUMBER alongside the string, and this is the only thing that reads it. The
       string parse stays as the fallback for a cached page that predates it. */
    const liveEur = window.ILS && window.ILS._leaderEurValue;
    if (typeof liveEur === 'number' && isFinite(liveEur) && liveEur > 0) return liveEur;
    const liveCached = window.ILS && window.ILS._leaderLiveValue;
    if (liveCached) return parseLeaderEur(liveCached);
    const onPage = document.getElementById('weekly_leader_amount');
    const row = document.querySelector('[data-ils-cd-leader-row]');
    const amt = document.querySelector('[data-ils-cd-leader]');
    let raw = '';
    if (onPage) raw = (onPage.textContent || '').trim();
    if (!raw && row) raw = (row.getAttribute('data-ils-cd-leader-original') || '').trim();
    if (!raw && amt) raw = (amt.textContent || '').trim();
    return parseLeaderEur(raw);
  }

  const HEART_SVG_INLINE = '<svg class="ils-cd__lead-heart" viewBox="0 0 188.1213 188.1213" aria-hidden="true">'
    + '<path transform="translate(-1389.3353,-1889.2884)" '
    + 'd="M1483.396,1929.109c-23.006-49.847-98.417-24.924-93.863,29.317,4.465,53.191,60.632,60.152,93.863,106.095,33.232-45.943,89.398-52.905,93.863-106.095,4.553-54.241-70.857-79.165-93.863-29.317Z" '
    + 'fill="#C60404"/></svg>';

  let leaderInited = false;
  const ILS_LEADER_FIRED_KEY = 'ils_leader_confetti_fired';

  /* TASK-2116 — hold confetti / sessionStorage bookkeeping until the live
     leader value from the Cloudflare Worker has landed in the DOM.
     Rationale: the Liquid-rendered `data-ils-cd-leader-original` is a stale
     shop-metafield fallback; custom-weekly-leader-live.js overwrites it
     asynchronously after page load. Without gating, the first renderLeaderState()
     runs against the stale value (state='below'), clears the sessionStorage
     "was leading" flag, then wireMorphObserver re-fires renderLeaderState()
     after the live value paints (state='leading') and confetti replays. */
  let liveLeaderReady = false;
  const LIVE_LEADER_TIMEOUT_MS = 2500;

  function armLiveLeaderGate(onReady) {
    if (liveLeaderReady) { onReady(); return; }
    if (window.ILS && window.ILS._leaderLiveDone) {
      liveLeaderReady = true;
      onReady();
      return;
    }
    let done = false;
    function fire() {
      if (done) return;
      done = true;
      liveLeaderReady = true;
      onReady();
    }
    document.addEventListener('ils:leader-live-ready', fire, { once: true });
    setTimeout(fire, LIVE_LEADER_TIMEOUT_MS);
  }

  function getWasLeading() {
    try { return sessionStorage.getItem(ILS_LEADER_FIRED_KEY) === '1'; }
    catch (e) { return false; }
  }
  function setWasLeading(v) {
    try {
      if (v) sessionStorage.setItem(ILS_LEADER_FIRED_KEY, '1');
      else sessionStorage.removeItem(ILS_LEADER_FIRED_KEY);
    } catch (e) {}
  }

  function buildStimulusHtml(needEur) {
    return ''
      + '<span class="ils-cd__lead-stim-dot" aria-hidden="true">⚡</span>'
      /* TASK-5856 — this was the ONE rwFmt() call still substituting a bare
         money2() number and relying on the template's own euro sign. Now that
         rwFmt() routes every template through ILSMoney.dropSign(), that sign is
         gone, so a bare number here would have rendered "Just <b>13,22</b> more
         to become this week's leader!" with NO currency at all — on EVERY
         market, EUR included. Found by group J of scripts/task-5856-tests.mjs,
         which executes the real rwMoney->rwFmt composition instead of
         inspecting the template. rwMoney() supplies its own sign (or the
         market's own format), which is the contract every other call site
         already follows. */
      + rwFmt(rwI18n().leaderStim, { amount: rwDelta(needEur) });
  }
  function buildLeaderPopupHtml() {
    return ''
      + '<span class="ils-cd__lead-pop-heart ils-cd__lead-pop-heart--l">' + HEART_SVG_INLINE + '</span>'
      + '<div class="ils-cd__lead-pop-txt">'
        + '<div class="ils-cd__lead-pop-title">' + rwI18n().leaderWinTitle + '</div>'
        + '<div class="ils-cd__lead-pop-sub">' + rwI18n().leaderWinSub + '</div>'
      + '</div>'
      + '<span class="ils-cd__lead-pop-heart ils-cd__lead-pop-heart--r">' + HEART_SVG_INLINE + '</span>';
  }

  /* TASK-2136 — confetti timing gate (owner still saw confetti BEFORE
     the green "Added to cart" plate after TASK-2118 → TASK-2131 fixes).

     Root cause proven by real headless-Chromium timeline capture
     (see reports/2136-bug2 timeline runs):
       t=+4287 ms   user click on PDP Add-to-cart (pending → true)
       t=+4862 ms   /cart/add.js resolves → Dawn publishes cartUpdate →
                    renderLeaderState → fireConfettiWhenReady is called
                    (pending is TRUE at this point → correctly enters
                    the wait-for-bump-done branch and arms a 1500 ms
                    safety timer)
       t=+5855 ms   fly animation lands → tryRunBump plays the badge
                    bump; markAddAnimResolved fires at +180 ms inside
                    the bump sequence (pending → false @ ~+6485 ms),
                    bump-done fires at +380 ms (bump-done @ ~+6685 ms)
       t=+6362 ms   the 1500 ms safety timer expires (armed at 4862);
                    fires confetti with the intended 240 ms delay
       t=+6805 ms   confetti layer appended → particles begin fading in
       t=+7166 ms   real bump-done finally fires → toast is added

     Net effect: the safety was armed BEFORE the full click→fetch→fly→
     bump chain could complete. Combined with markAddAnimResolved
     firing 200 ms before bump-done, confetti visually preceded the
     toast by ~360 ms.

     Fix (belt-and-suspenders, both sides needed):
       1. custom-cart-icon-v2.js — moved markAddAnimResolved into the
          same 380 ms setTimeout that dispatches bump-done, so `pending`
          truly stays TRUE from click all the way through bump-done.
       2. This file — safety timer raised from 1500 → 3500 ms so it
          cannot expire before bump-done even on slow networks. Also
          delay bumped from 240 → 260 ms to sit just AFTER the toast's
          200 ms opacity transition finishes (rAF + 200 ms), matching
          the "just after the plate" cadence the owner asked for.

     Paths without a pending add-anim (stepper, variant swap, remove)
     still fire immediately — no toast in those cases. */
  const CONFETTI_TOAST_SYNC_DELAY_MS = 260;
  const CONFETTI_SAFETY_TIMEOUT_MS = 3500;
  function fireConfettiWhenReady() {
    const anim = window.ILS && window.ILS._cartAddAnim;
    if (!anim || !anim.pending) {
      fireConfetti();
      return;
    }
    let done = false;
    let safety = null;
    function trigger() {
      if (done) return;
      done = true;
      document.removeEventListener('ils:add:bump-done', trigger);
      if (safety) clearTimeout(safety);
      setTimeout(fireConfetti, CONFETTI_TOAST_SYNC_DELAY_MS);
    }
    document.addEventListener('ils:add:bump-done', trigger);
    safety = setTimeout(trigger, CONFETTI_SAFETY_TIMEOUT_MS);
  }

  function fireConfetti() {
    const layer = document.createElement('div');
    layer.className = 'ils-cd__confetti';
    layer.setAttribute('aria-hidden', 'true');
    const colors = ['#C60404', '#E8C39A', '#F2B705', '#2BB34B', '#F6EBDB', '#FFFFFF'];
    const parts = [];

    const FALL_COUNT = 130;
    const BURST_COUNT = 50;
    const TOTAL_MS = 4000;

    for (let i = 0; i < FALL_COUNT; i++) {
      const lane = i / FALL_COUNT;
      const left = (lane * 100 + (Math.random() - 0.5) * (100 / FALL_COUNT) * 1.6);
      const delay = Math.random() * 1.2;
      const dur = 2.6 + Math.random() * 1.2;
      const rot = Math.floor(Math.random() * 360);
      const color = colors[i % colors.length];
      const w = 3 + Math.floor(Math.random() * 4);
      const h = Math.max(3, Math.floor(w * (1.2 + Math.random() * 0.7)));
      const drift = (Math.random() * 200 - 100) | 0;
      const spin = (Math.random() * 900 - 450) | 0;
      parts.push(
        '<i class="is-fall" style="left:' + left.toFixed(2) + '%;background:' + color +
        ';width:' + w + 'px;height:' + h + 'px;animation-delay:' + delay.toFixed(2) + 's;' +
        'animation-duration:' + dur.toFixed(2) + 's;' +
        '--ils-rot:' + rot + 'deg;' +
        '--ils-drift:' + drift + 'px;' +
        '--ils-spin:' + spin + 'deg"></i>'
      );
    }

    for (let j = 0; j < BURST_COUNT; j++) {
      const angle = Math.random() * Math.PI * 2;
      const distVw = 28 + Math.random() * 24;
      const bx = Math.cos(angle) * distVw;
      const by = Math.sin(angle) * distVw - 8;
      const delay = Math.random() * 0.15;
      const dur = 2.4 + Math.random() * 1.4;
      const rot = Math.floor(Math.random() * 360);
      const spin = (Math.random() * 1200 - 600) | 0;
      const drift = (Math.random() * 120 - 60) | 0;
      const color = colors[j % colors.length];
      const w = 3 + Math.floor(Math.random() * 4);
      const h = Math.max(3, Math.floor(w * (1.2 + Math.random() * 0.7)));
      parts.push(
        '<i class="is-burst" style="background:' + color +
        ';width:' + w + 'px;height:' + h + 'px;animation-delay:' + delay.toFixed(2) + 's;' +
        'animation-duration:' + dur.toFixed(2) + 's;' +
        '--ils-rot:' + rot + 'deg;' +
        '--ils-drift:' + drift + 'px;' +
        '--ils-spin:' + spin + 'deg;' +
        '--ils-burst-x:' + bx.toFixed(2) + 'vw;' +
        '--ils-burst-y:' + by.toFixed(2) + 'vh"></i>'
      );
    }

    layer.innerHTML = parts.join('');
    document.body.appendChild(layer);
    setTimeout(function () {
      if (layer.parentNode) layer.parentNode.removeChild(layer);
    }, TOTAL_MS);
  }

  function renderLeaderState(cartEur) {
    const leaderEur = getLeaderEur();
    let state;
    if (!leaderEur || !Number.isFinite(leaderEur) || leaderEur <= 0) {
      state = 'below';
    } else if (cartEur > leaderEur) {
      state = 'leading';
    } else if (cartEur >= leaderEur * 0.8) {
      state = 'approaching';
    } else {
      state = 'below';
    }

    /* TASK-2117 — run confetti / leaderInited bookkeeping synchronously on
       every render so add-to-cart crossings fire confetti immediately (no
       longer wait for the live-leader gate).
       TASK-2116 protection preserved: only allow CLEARING the sessionStorage
       "was leading" flag once the live leader is authoritative. Setting it
       to true is safe unconditionally — it just suppresses future duplicate
       confetti in the same session. This blocks the stale-Liquid leader
       from wiping the flag before the Cloudflare Worker value has painted,
       which is what caused the TASK-2116 repeat-confetti regression. */
    const wasLeadingPersisted = getWasLeading();
    if (state === 'leading' && !wasLeadingPersisted && leaderInited) {
      fireConfettiWhenReady();
    }
    if (state === 'leading') {
      setWasLeading(true);
    } else if (liveLeaderReady) {
      setWasLeading(false);
    }
    leaderInited = true;

    const leaderRow = document.querySelector('[data-ils-cd-leader-row]');
    const leaderLbl = document.querySelector('[data-ils-cd-leader-lbl]');
    const leaderAmt = document.querySelector('[data-ils-cd-leader]');
    if (leaderRow && leaderLbl && leaderAmt) {
      if (state === 'leading') {
        leaderLbl.textContent = rwI18n().leaderLabelYou;
        /* TASK-5856 — REPORT-5848 D8: cartEur is a correctly converted EUR value,
           but it was printed with a euro sign beside a kr/zl/Ft subtotal. It is
           the shopper's own basket, i.e. a fact and not a target, so rwFact()
           formats it without pushing it up a rounding grid. */
        leaderAmt.textContent = rwFact(cartEur);
      } else {
        leaderLbl.textContent = rwI18n().leaderLabel;
        /* TASK-2129 — after losing the lead, paint the LIVE cached value
           first. Prior code read `data-ils-cd-leader-original` which is
           the Liquid €0,00 fallback right after an innerHTML swap; if
           applyLeaderLive hadn't run yet (or _leaderLiveValue arrived
           after this render) the row briefly showed €0,00. */
        const liveCached = window.ILS && window.ILS._leaderLiveValue;
        const original = liveCached
          || leaderRow.getAttribute('data-ils-cd-leader-original')
          || '';
        if (original) leaderAmt.textContent = original;
      }
    }

    const stimEls = document.querySelectorAll('[data-ils-cd-stim]');
    const popEls = document.querySelectorAll('[data-ils-cd-pop]');
    if (!stimEls.length && !popEls.length) return;

    if (state === 'leading') {
      stimEls.forEach((el) => { el.hidden = true; el.innerHTML = ''; });
      popEls.forEach((el) => {
        if (!el.innerHTML) el.innerHTML = buildLeaderPopupHtml();
        el.hidden = false;
      });
    } else if (state === 'approaching') {
      popEls.forEach((el) => { el.hidden = true; el.innerHTML = ''; });
      stimEls.forEach((el) => {
        const need = Math.max(0, leaderEur - cartEur);
        el.innerHTML = buildStimulusHtml(need);
        el.hidden = false;
      });
    } else {
      stimEls.forEach((el) => { el.hidden = true; el.innerHTML = ''; });
      popEls.forEach((el) => { el.hidden = true; el.innerHTML = ''; });
    }
  }

  function init() {
    wireCartIconOpener();
    wireCartUpdateSub();
    wireRemoveConfirm();
    wireMinusLock();
    wireKeyBasedQty();
    wireStepperMinusVisibility();
    wireVariantSelect();
    wireWeightPopup();
    wireUpsellAdd();
    wireContinueShopping();
    wireCheckoutGate();       // TASK-3340 — Checkout → /pages/delivery-selector
    wireOpenCloseSideEffects();
    wireMorphObserver();
    /* TASK-2116 — hydrateLeader first so the row shows *some* value while
       we wait for the live fetch, then defer the very first initRewards()
       (which is what calls renderLeaderState()) until either the live
       leader has settled or the safety timeout fires. */
    hydrateLeader();
    armLiveLeaderGate(function () {
      initRewards();
    });
    fetchAndUpdateCartCount();
    loadUpsell();
    initUpsellWave();
    initCdPayMarquee();
    wireCdPayMarqueeResize();
  }

  /* TASK-2407 — expose the minimum surface that custom-gift-logic.js needs
     so the atomic gift-swap can (a) morph the drawer with a single final
     section HTML and (b) keep the header cart bubble in sync without every
     caller re-fetching /cart.js. Kept read-only — helpers used only by
     other custom-*.js modules; no schema for base-theme code. */
  window.ILS = window.ILS || {};
  if (typeof window.ILS.morphCartDrawer !== 'function') window.ILS.morphCartDrawer = morphCartDrawer;
  if (typeof window.ILS.updateCartCount !== 'function') window.ILS.updateCartCount = updateCartCount;
  if (typeof window.ILS.fetchAndUpdateCartCount !== 'function') window.ILS.fetchAndUpdateCartCount = fetchAndUpdateCartCount;
  if (typeof window.ILS.updateRewardsFromTotalCents !== 'function') window.ILS.updateRewardsFromTotalCents = updateRewardsFromTotalCents;

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
